// Master Apps · Apresentação do portfólio — deck em sequência + exportação PDF.
//
// Porte do PowerPoint "Dados, IA e Inovação — Desenvolvimentos" para dentro do
// produto: mesmos slides, mesma tipografia de blocos, mesmas coordenadas (ver
// `lib/deck.ts`), só que alimentados pelo cadastro real em vez de digitados à
// mão. O que o deck manual errava e aqui não erra mais:
//
//  · os números da capa de visão eram fixos (28) enquanto o deck já listava 46
//    plataformas — agora saem da contagem real, por status;
//  · a numeração repetia ("Plataforma de Recolhimento" era nº 04 e nº 31) —
//    agora é a posição no deck;
//  · havia slide com o campo de status preenchido com a ÁREA ("●RH") e slide
//    sem status nenhum — agora a pílula vem do status cadastrado, sempre.
//
// O PDF sai pela impressão nativa do navegador: o `@media print` abaixo revela
// TODOS os slides empilhados, um por página de 1440×810, sem a moldura de
// navegação. Zero dependência nova e o texto sai vetorial (pesquisável), o que
// nenhuma biblioteca de canvas entrega.
import { diasUteisDoMesDe, horasDeProjeto } from '@masterapps/shared';
import {
  type CSSProperties,
  Fragment,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { AppPhoto, applicationImageUrls } from '@/components/app-visuals';
import {
  IconCheck,
  IconChevronR,
  IconFilter,
  IconGrid,
  IconSearch,
  IconX,
} from '@/components/icons';
import { SelectMenu } from '@/components/select-menu';
import { useSetApresentacaoDesabilitada } from '@/hooks/use-applications';
import {
  type CotacaoDolar,
  converterParaBrl,
  costsByRepoSlug,
  type RailwayProjectCost,
  repoSlug,
  useRailwayCosts,
} from '@/hooks/use-railway-costs';
import { byViewport, useViewport } from '@/hooks/use-viewport';
import { APP_TYPES, type App } from '@/lib/catalog-config';
import {
  appInDeckPeriod,
  DECK,
  DECK_ABOUT,
  DECK_GEO,
  DECK_H,
  DECK_MONTHS_PT,
  DECK_RAIL_META,
  DECK_RAIL_STACK,
  DECK_RETORNO_MAX,
  DECK_SEM_AREA,
  DECK_STATUS,
  DECK_STATUS_ORDER,
  DECK_W,
  type DeckPeriod,
  deckAplicarOrdem,
  deckAreaOf,
  deckAvailableYears,
  deckCounts,
  deckDocumentTitle,
  deckMoverNaOrdem,
  deckPeriodTitle,
  deckPeriodYears,
  deckSlideNumber,
  deckStatusOf,
  deckTitleSize,
  normalizeDeckPeriod,
} from '@/lib/deck';
import { formatBrlBR, formatDateBR } from '@/lib/format';
import { fontSans } from '@/lib/theme';
import { useDeckOrder } from '@/store/deck-order';

const CRITICALITY_LABELS: Record<string, string> = {
  alta: 'Crítica',
  media: 'Média',
  baixa: 'Baixa',
};

/**
 * Sangria do slide, em px do slide (1440×810).
 *
 * A capa desenha dois ornamentos de canto que saem pela borda ESQUERDA de
 * propósito — o mais fundo começa em `left:-60` com 68px de largura (ver
 * `DeckRedBackdrop`) — e o `overflow:hidden` do slide recorta o excesso. Num
 * monitor sobra respiro e a caixa deles cai dentro da tela; no celular, com o
 * slide colado na borda, ela vaza para fora da viewport.
 *
 * Por isso, no compacto, o slide é escalado para caber em 1440 + 2×68 em vez de
 * 1440: o desenho é o mesmo (nenhuma coordenada do PowerPoint muda), o slide
 * ganha uma moldura escura do tamanho exato da sangria e nada dele fica fora da
 * tela. 68 e não 60 porque a folga precisa cobrir também o arredondamento de
 * meio pixel do próprio recuo (`60 + 1/escala`, e a escala no compacto nunca
 * passa de ~0,5).
 */
const DECK_BLEED = 68;

/**
 * Regras que NÃO cabem em style inline: `@page` (tamanho da folha), `@media
 * print` e o seletor de slide ativo. Fica num <style> montado junto da tela —
 * some quando a tela desmonta, então o `@page` de 1440×810 nunca vaza para a
 * impressão das outras telas do produto.
 */
const DECK_CSS = `
.deck-root{position:fixed;inset:0;display:flex;flex-direction:column;background:#0a0a0b;font-family:${fontSans};}
/* ATENÇÃO: sem acento grave neste bloco — ele vive num template literal.
   overflow:clip DEPOIS de hidden (navegador que não conhece a palavra fica com o
   hidden): transform:scale encolhe o DESENHO do slide, não a caixa de layout —
   ela continua com 1440px. Com hidden, o palco virava um contêiner rolável de
   916px dentro de 375px de tela: rolagem que ninguém vê, mas existe (dá para
   rolar por script, e é o que o medidor de viewport acusa). O clip recorta igual
   e NÃO cria área de rolagem. O padding fica aqui porque a escala do slide sai da
   caixa de BORDA do palco: o recuo não muda o tamanho do slide, só a moldura. */
.deck-stage{flex:1;min-height:0;display:flex;align-items:center;justify-content:center;overflow:hidden;overflow:clip;padding:18px;}
.deck-viewport{position:relative;flex:none;width:${DECK_W}px;height:${DECK_H}px;background:#fff;transform:scale(var(--deck-scale,1));transform-origin:center center;box-shadow:0 30px 90px rgba(0,0,0,.55);}
.deck-slide{position:absolute;inset:0;width:${DECK_W}px;height:${DECK_H}px;overflow:hidden;background:#fff;display:none;}
.deck-slide[data-active='true']{display:block;}
/* Páginas que SÓ existem no papel: uma por tela adicional da galeria (ver
   ShotPrintSlide). Ficam fora da tela o tempo todo — a navegação conta slides
   pelo plano em JS, então elas não entram na contagem nem no índice — e o
   @media print lá embaixo as revela, uma por página. */
.deck-print-slide{display:none;}
/* Tela cheia de verdade: o chrome some e o palco vira a tela inteira. Só o
   controle flutuante (deck-fs-nav) fica, quase invisível até o hover. */
.deck-root[data-fs='true'] .deck-chrome{display:none!important;}
.deck-root[data-fs='true'] .deck-stage{padding:0;}
.deck-root[data-fs='true'] .deck-viewport{box-shadow:none;}
.deck-fs-nav{position:fixed;left:50%;bottom:22px;transform:translateX(-50%);display:flex;align-items:center;gap:4px;padding:6px 10px;border-radius:999px;background:rgba(10,10,11,0.72);border:1px solid rgba(255,255,255,0.14);backdrop-filter:blur(10px);opacity:0.25;transition:opacity 200ms;z-index:15;}
.deck-fs-nav:hover,.deck-fs-nav:focus-within{opacity:1;}
.deck-fs-btn{width:32px;height:32px;border-radius:999px;border:none;background:transparent;color:#fff;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;transition:background 140ms;}
.deck-fs-btn:hover{background:rgba(255,255,255,0.14);}
/* ── Modal de filtros ─────────────────────────────────────────────────── */
@keyframes deck-modal-in{from{opacity:0;transform:translateY(10px) scale(0.98);}to{opacity:1;transform:translateY(0) scale(1);}}
@keyframes deck-overlay-in{from{opacity:0;}to{opacity:1;}}
.deck-modal-backdrop{animation:deck-overlay-in 100ms ease-out;}
.deck-modal{animation:deck-modal-in 130ms ease-out;}
/* Seleção precisa responder na hora: 60ms é feedback, 140ms era "animação". */
.deck-fpill{height:32px;padding:0 13px;border-radius:999px;border:1px solid rgba(255,255,255,0.13);background:rgba(255,255,255,0.03);color:rgba(255,255,255,0.82);font-size:12px;font-weight:600;font-family:inherit;cursor:pointer;display:inline-flex;align-items:center;gap:7px;white-space:nowrap;transition:border-color 60ms,background 60ms,color 60ms;}
.deck-fpill:hover:not(:disabled){border-color:rgba(255,255,255,0.38);background:rgba(255,255,255,0.07);}
.deck-fpill:disabled{opacity:0.3;cursor:default;}
.deck-fpill[data-on='true']{border-color:var(--pill,#C50905);background:var(--pill,#C50905);color:#fff;box-shadow:0 4px 14px -4px var(--pill,#C50905);}
.deck-fpill[data-on='true']:hover{filter:brightness(1.08);}
.deck-finput{display:flex;align-items:center;gap:10px;height:40px;padding:0 14px;border-radius:10px;border:1px solid rgba(255,255,255,0.13);background:rgba(255,255,255,0.04);transition:border-color 140ms,box-shadow 140ms;}
.deck-finput:focus-within{border-color:#C50905;box-shadow:0 0 0 3px rgba(197,9,5,0.25);}
.deck-fclose{width:30px;height:30px;border-radius:8px;border:1px solid rgba(255,255,255,0.14);background:transparent;color:rgba(255,255,255,0.7);cursor:pointer;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;transition:background 140ms,color 140ms;}
.deck-fclose:hover{background:rgba(255,255,255,0.1);color:#fff;}
@media print{
  @page{size:${DECK_W}px ${DECK_H}px;margin:0;}
  html,body{margin:0!important;padding:0!important;background:#fff!important;}
  .deck-chrome,.deck-overlay,.deck-carousel-ui,[data-sonner-toaster]{display:none!important;}
  /* Papel não tem carrossel: no PDF a captura do slide da plataforma é SEMPRE a
     capa, não a tela em que o apresentador tiver parado — as demais saem nas
     páginas extras, em ordem. Sem isto, imprimir no meio da apresentação
     repetiria uma tela e sumiria com a abertura. */
  .deck-shot-frame{display:none!important;}
  .deck-shot-capa{display:flex!important;}
  .deck-print-slide{display:block!important;position:relative;width:${DECK_W}px;height:${DECK_H}px;overflow:hidden;background:#fff;break-after:page;page-break-after:always;}
  .deck-root{position:static!important;display:block!important;background:#fff!important;}
  .deck-stage{display:block!important;overflow:visible!important;padding:0!important;}
  .deck-viewport{position:static!important;width:auto!important;height:auto!important;transform:none!important;box-shadow:none!important;}
  .deck-slide{position:relative!important;inset:auto!important;display:block!important;break-after:page;page-break-after:always;}
  .deck-slide:last-of-type{break-after:auto;page-break-after:auto;}
  .deck-slide,.deck-slide *,.deck-print-slide,.deck-print-slide *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;}
}
`;

// ── Regras do painel, do card cinza e do plano de slides ───────────────
//
// Derivação pura, sem DOM — moraria bem em `lib/deck.ts` se não fosse o gate de
// cobertura POR ARQUIVO do `bunfig.toml`: aquele módulo está em 100% e o teste
// que o cobre é de outra frente, então função nova sem teste lá reprova
// `bun test` inteiro. Aqui (`.tsx`) o `coveragePathIgnorePatterns` já exclui.

const preenchido = (v: string | null | undefined): boolean =>
  typeof v === 'string' && v.trim() !== '';

/** Número pt-BR sem casa decimal fantasma: 8 sai "8" e 7,5 sai "7,5". */
const numeroBR = (valor: number): string =>
  valor.toLocaleString('pt-BR', { maximumFractionDigits: 2 });

/**
 * Quantos caracteres cabem numa linha de `largura` px no corpo `size`.
 *
 * Estimativa pela largura média do caractere (`charRatio`), não medição no DOM:
 * o slide é desenhado em coordenadas absolutas, então quem decide quantas linhas
 * um campo ocupa precisa da resposta ANTES de pintar — não há como medir o
 * parágrafo e reagir depois sem o texto saltar na tela projetada.
 */
const charsPorLinha = (largura: number, size: number, charRatio: number): number =>
  Math.max(1, Math.floor(largura / (size * charRatio)));

/** Linhas que um texto ocupa numa caixa de `chars` caracteres por linha. */
const linhasDe = (texto: string, chars: number): number =>
  Math.max(1, Math.ceil(texto.trim().replace(/\s+/g, ' ').length / chars));

/**
 * Comprimento do texto em "caracteres de prosa" — a unidade em que
 * `DECK_RETORNO_MAX` foi medido.
 *
 * Maiúscula pesa mais porque ocupa mais: o mesmo parágrafo em caixa alta cabe
 * num quinto a menos de letras que em caixa mista. `c !== c.toLowerCase()` é o
 * teste de caixa que vale em qualquer alfabeto — dígito, espaço e pontuação
 * respondem `false` e continuam valendo 1.
 */
const pesoDeTexto = (texto: string): number =>
  // Arredondado de propósito: o peso da maiúscula é fracionário, e sem isto um
  // texto de exatamente `DECK_RETORNO_MAX` caracteres com a inicial em caixa
  // alta pesaria 250,3 e ganharia um aviso de corte que não existe.
  Math.round(
    [...texto].reduce(
      (soma, c) => soma + (c !== c.toLowerCase() ? DECK_ABOUT.maiusculaPeso : 1),
      0,
    ),
  );

/**
 * Esforço do projeto para o painel: SÓ O TOTAL DE HORAS.
 *
 * O total vem de `horasDeProjeto` (@masterapps/shared), a MESMA conta do
 * formulário e da ficha: reimplementar a multiplicação renderia arredondamentos
 * diferentes para o mesmo dado em três telas (13 × 7,7 dá 100,10000000000001 em
 * ponto flutuante cru).
 *
 * A CONTA que gerava o total ("13 dias × 7,7 h/dia") saiu do deck a pedido do
 * dono do produto: numa projeção para a diretoria o que se compara entre
 * projetos é o tamanho do esforço, e a razão dias/hora abria uma discussão de
 * apontamento no meio da apresentação. Ela continua na ficha da aplicação e no
 * cadastro, onde tem quem a leia. Sem as DUAS parcelas não há total — arbitrar
 * a outra metade (supor 8 h/dia) inventaria número numa projeção.
 */
function deckEsforcoHoras(app: App): string | null {
  const total = horasDeProjeto(app);
  return total === null ? null : `${numeroBR(total)} h`;
}

/** O que o carimbo "Acessos no mês" mostra para esta aplicação. */
interface DeckAcessos {
  /** "12/22": dias ÚTEIS do mês corrente com pelo menos um login, sobre os dias úteis do mês — ou "—". */
  valor: string;
  /** Tooltip do valor: explica o "—" (coleta que falhou, ou que nunca rodou). */
  titulo: string | undefined;
}

/**
 * Dias ÚTEIS (seg–sex) do mês do calendário — no fuso de Recife (UTC-3, sem
 * horário de verão), que é o "dia" que a coleta usa para contar. Um ISO de
 * 01/03 01:30Z é 28/02 22:30 em Recife: o mês certo é fevereiro. Fim de semana
 * saiu do numerador E do denominador por pedido explícito: "12/22" é legível
 * como "12 de 22 dias de trabalho"; "12/30" fazia todo sistema parecer parado
 * um terço do mês.
 */
export function diasDoMesDeColeta(iso: string): number {
  // Regra única (seg–sex fora dos feriados) em `@masterapps/shared`, a mesma
  // que a API usa para contar os dias com acesso.
  return diasUteisDoMesDe(iso);
}

/**
 * Dias com acesso no mês corrente, coletados AUTOMATICAMENTE do Clerk de cada
 * aplicação pela API — nada disto é digitado no cadastro. O pedido foi este
 * número, e só ele, na apresentação: "12/22" diz em quantos dias úteis alguém
 * entrou na plataforma, que é o que a sala quer saber; totais de sessões e de
 * usuários ficam no detalhe da ficha.
 *
 * "—" em três casos, e em nenhum deles sai 0: a coleta nunca rodou (colunas
 * todas nulas — aplicação sem Clerk próprio, por exemplo), o último ciclo
 * falhou (`acessosErro`, que vai para o tooltip para quem apontar na tela), ou
 * não há carimbo de coleta. Zero só aparece quando o Clerk de fato respondeu
 * zero: num slide para a diretoria, "0/22" numa plataforma em produção é
 * acusação, e "—" é só "não medido".
 */
function deckAcessos(app: App): DeckAcessos {
  if (app.acessosErro) {
    return { valor: '—', titulo: `Última coleta falhou: ${app.acessosErro}` };
  }
  if (!app.acessosColetadoEm || typeof app.acessosDiasMes !== 'number') {
    return { valor: '—', titulo: 'Acessos ainda não coletados do Clerk' };
  }
  const dias = diasDoMesDeColeta(app.acessosColetadoEm);
  return { valor: `${String(app.acessosDiasMes).padStart(2, '0')}/${dias}`, titulo: undefined };
}

/**
 * Plano de slides do deck: capa, visão, uma entrada por plataforma e o
 * encerramento. Uma plataforma, um slide — o slide dedicado "Por que esta
 * plataforma existe" foi removido a pedido do dono do produto, e com ele o
 * campo `projetoBeneficio` — que depois saiu do produto inteiro (cadastro,
 * ficha e coluna do banco), então não há para onde ele voltar.
 *
 * O `appIndex` continua separado da posição do slide, e não é zelo à toa: é ele
 * que numera a plataforma ("Plataforma nº 07"). Enquanto o deck era feito à
 * mão, a numeração vinha da página e repetia (a mesma plataforma era nº 04 e
 * nº 31); manter a separação deixa a capa e o encerramento fora da conta e
 * sobrevive ao próximo slide intercalado que alguém inventar.
 *
 * Exportado porque o LOBBY anuncia quantos slides o recorte rende: contar
 * "plataformas + 3" lá e aqui daria dois números para a mesma pergunta.
 */
type DeckPlanItem =
  | { kind: 'cover' }
  | { kind: 'vision' }
  | { kind: 'closing' }
  | { kind: 'platform'; appIndex: number };

export function deckSlidePlan(apps: readonly App[]): DeckPlanItem[] {
  const plano: DeckPlanItem[] = [{ kind: 'cover' }, { kind: 'vision' }];
  apps.forEach((_app, appIndex) => {
    plano.push({ kind: 'platform', appIndex });
  });
  plano.push({ kind: 'closing' });
  return plano;
}

// ── Peças compartilhadas ────────────────────────────────────────────────────

/**
 * Assinatura Masterboi oficial (wordmark horizontal com o boi) — a variante
 * vermelha vive sobre fundo claro, a branca sobre o vermelho da capa. Os PNGs
 * têm fundo transparente de verdade, então nada de pastilha branca por trás.
 * `size` é a ALTURA da assinatura; a largura segue a proporção do arquivo.
 */
function DeckLogo({ tone, size = 22 }: Readonly<{ tone: 'red' | 'white'; size?: number }>) {
  return (
    <img
      src={`/assets/masterboi-wordmark-${tone}.png`}
      alt="Masterboi"
      style={{ display: 'block', height: size * 1.15, width: 'auto' }}
    />
  );
}

/** Rótulo miúdo em caixa alta — o "ÁREA SOLICITANTE" dos slides. */
function DeckLabel({
  children,
  top,
  left,
  color,
}: Readonly<{
  children: React.ReactNode;
  top: number;
  left: number;
  color: string;
}>) {
  return (
    <div
      style={{
        position: 'absolute',
        left,
        top,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.11em',
        textTransform: 'uppercase',
        color,
        lineHeight: 1,
      }}
    >
      {children}
    </div>
  );
}

/** Pílula de status — retângulo cheio na cor do status, texto branco. */
function DeckStatusPill({
  label,
  color,
  width,
  height = 43.5,
  fontSize = 14.3,
  left,
  top,
}: Readonly<{
  label: string;
  color: string;
  width?: number;
  height?: number;
  fontSize?: number;
  left: number;
  top: number;
}>) {
  return (
    <div
      style={{
        position: 'absolute',
        left,
        top,
        width,
        height,
        background: color,
        borderRadius: 3,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: `0 ${height * 0.38}px`,
        color: '#fff',
        fontSize,
        fontWeight: 700,
        letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: fontSize * 0.42,
          height: fontSize * 0.42,
          borderRadius: '50%',
          background: '#fff',
          flexShrink: 0,
        }}
      />
      {label}
    </div>
  );
}

/**
 * Painel vermelho da esquerda — a espinha do deck: kicker, título, régua e
 * rodapé são idênticos no slide de visão e nos de plataforma; só o miolo muda.
 */
function DeckRail({
  kicker,
  title,
  titleSize,
  footer,
  watermark,
  children,
}: Readonly<{
  kicker: string;
  title: string;
  titleSize: number;
  footer: string;
  watermark?: string;
  children?: React.ReactNode;
}>) {
  const { railW, edgeW, railPad, ruleY, ruleW, ruleH, footerY } = DECK_GEO;
  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: railW,
        height: DECK_H,
        background: DECK.red,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: edgeW,
          background: DECK.redEdge,
        }}
      />

      {/* Número da plataforma em marca d'água, sangrando pela borda direita. */}
      {watermark && (
        <div
          style={{
            position: 'absolute',
            right: -10,
            bottom: -26,
            // 170, e não os 200 de antes: com os quatro campos de projeto
            // preenchidos a pilha de identidade chega perto do rodapé, e o
            // número gigante ficava POR TRÁS do "Custo mensal" — visto na
            // projeção. Menor, o topo da marca d'água desce de 636 para 666 e
            // os carimbos passam limpos. Ela é ornamento; o dado, não.
            fontSize: 170,
            fontWeight: 800,
            lineHeight: 1,
            letterSpacing: '-0.04em',
            color: DECK.redWatermark,
            userSelect: 'none',
          }}
        >
          {watermark}
        </div>
      )}

      <DeckLabel top={72} left={railPad} color={DECK.redLabel}>
        {kicker}
      </DeckLabel>

      <div
        style={{
          position: 'absolute',
          left: railPad - 7,
          top: 98,
          width: railW - railPad - 24,
          fontSize: titleSize,
          fontWeight: 800,
          lineHeight: 1.1,
          letterSpacing: '-0.022em',
          color: '#fff',
          // Rede de segurança: nome de uma palavra só, longo demais até para o
          // menor corpo, quebra em vez de sangrar pela borda do painel.
          overflowWrap: 'break-word',
        }}
      >
        {title}
      </div>

      <div
        style={{
          position: 'absolute',
          left: 50,
          top: ruleY,
          width: ruleW,
          height: ruleH,
          background: '#fff',
        }}
      />

      {children}

      <DeckLabel top={footerY} left={railPad} color={DECK.redLabel}>
        {footer}
      </DeckLabel>
    </div>
  );
}

/** Fundo vermelho da capa: listras horizontais sutis + ornamento de canto. */
function DeckRedBackdrop() {
  return (
    <>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: DECK.red,
          backgroundImage:
            'repeating-linear-gradient(180deg, rgba(255,255,255,0.035) 0px, rgba(255,255,255,0.035) 1px, transparent 1px, transparent 6px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: -8,
          top: -60,
          width: 86,
          height: 540,
          borderRight: '1.5px solid rgba(255,255,255,0.3)',
          borderBottom: '1.5px solid rgba(255,255,255,0.3)',
          borderBottomRightRadius: 70,
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: -60,
          top: 340,
          width: 68,
          height: 260,
          borderRight: '1.5px solid rgba(255,255,255,0.22)',
          borderBottom: '1.5px solid rgba(255,255,255,0.22)',
          borderBottomRightRadius: 58,
        }}
      />
    </>
  );
}

// ── Slides ──────────────────────────────────────────────────────────────────

function CoverSlide({ period, subtitle }: Readonly<{ period: DeckPeriod; subtitle: string }>) {
  const heading = deckPeriodTitle(period);
  // Com o intervalo ativo entra a terceira linha ("Janeiro a Junho de 2026"),
  // então o bloco inteiro sobe para continuar respirando acima da régua.
  const hasSub = !!heading.sub;
  return (
    <>
      <DeckRedBackdrop />
      <div style={{ position: 'absolute', right: 60, top: 48 }}>
        <DeckLogo tone="white" size={30} />
      </div>
      <div
        style={{
          position: 'absolute',
          left: 70,
          top: hasSub ? 428 : 486,
          fontSize: 70,
          fontWeight: 400,
          lineHeight: 1.1,
          color: '#fff',
          letterSpacing: '-0.02em',
        }}
      >
        {subtitle}
      </div>
      <div
        style={{
          position: 'absolute',
          left: 70,
          top: hasSub ? 514 : 572,
          fontSize: 90,
          fontWeight: 800,
          lineHeight: 1.1,
          color: '#fff',
          letterSpacing: '-0.03em',
        }}
      >
        {heading.title}
      </div>
      {heading.sub && (
        <div
          style={{
            position: 'absolute',
            left: 70,
            top: 632,
            fontSize: 56,
            fontWeight: 800,
            lineHeight: 1.1,
            color: '#fff',
            letterSpacing: '-0.02em',
            opacity: 0.92,
          }}
        >
          {heading.sub}
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          left: 72,
          top: 726,
          width: 240,
          height: 4,
          background: '#fff',
        }}
      />
    </>
  );
}

/** O que a tela de visão mostra sobre dinheiro: quanto e de quantas plataformas. */
interface GastoDaNuvem {
  /** Soma mensal em reais. `null` = custo ou cotação ausentes; a tela mostra "—". */
  totalBrl: number | null;
  /** Quantas plataformas do recorte realmente geram fatura de nuvem. */
  plataformas: number;
}

/**
 * Soma o custo mensal das plataformas do recorte e converte para reais.
 *
 * SOMA POR PROJETO DO RAILWAY, não por ficha: duas aplicações do inventário
 * podem apontar para o MESMO repositório (o front e a API de um monorepo, por
 * exemplo) e a fatura do Railway é uma só. Contar por ficha somaria o mesmo
 * projeto duas vezes e a apresentação abriria com um gasto inflado — por isso o
 * `vistos`, que também é o número de plataformas hospedadas que a tela declara.
 *
 * SOMA O RECORTE, não o portfólio inteiro: o apresentador escolhe quais
 * plataformas entram no deck, e o total de plataformas exibido no mesmo slide é
 * o do recorte. Um gasto que incluísse plataforma de fora não fecharia com o
 * número que a sala está vendo.
 *
 * Aplicação sem projeto no Railway não entra e não vira zero na contagem: ela
 * roda em servidor próprio, e é isso que a linha de apoio do slide diz.
 */
function gastoDaNuvem(
  apps: readonly App[],
  custoPorSlug: Map<string, RailwayProjectCost>,
  cotacao: CotacaoDolar | null | undefined,
  pronto: boolean,
): GastoDaNuvem {
  // Enquanto a consulta não voltou, TUDO é desconhecido — o mesmo motivo pelo
  // qual o custo por slide é "—": afirmar R$ 0,00 com o dado ausente é mentir.
  if (!pronto) return { totalBrl: null, plataformas: 0 };
  const vistos = new Set<string>();
  let usd = 0;
  for (const a of apps) {
    const slug = repoSlug(a.repo);
    if (!slug || vistos.has(slug)) continue;
    const custo = custoPorSlug.get(slug);
    if (!custo) continue;
    vistos.add(slug);
    usd += custo.estimadoMesUsd;
  }
  return { totalBrl: converterParaBrl(usd, cotacao), plataformas: vistos.size };
}

function VisionSlide({
  apps,
  period,
  nuvem,
}: Readonly<{ apps: App[]; period: DeckPeriod; nuvem: GastoDaNuvem }>) {
  const counts = deckCounts(apps);
  const years = deckPeriodYears(period);
  const phrase = period.mode === 'year' ? 'neste ano' : 'no período';
  const { contentX, contentW, footerY } = DECK_GEO;
  const gap = 20;
  const cardW = counts.length > 0 ? (contentW - gap * (counts.length - 1)) / counts.length : 0;
  /*
    Escala tipográfica do card, para o portfólio que usa TODAS as situações.
    Os corpos abaixo (pílula 11,3 · número 120 · manchete 14,3 · apoio 12) foram
    medidos no card de 170px, que é o que sobra quando cinco situações dividem a
    coluna de 930. Com a sexta (`pausado`) o card cai para 138px e nada disso
    cabe mais: a pílula "DESENVOLVIMENTO" é `nowrap` e sangraria pela borda, e o
    texto de apoio passaria de quatro linhas — indo parar por cima do rótulo
    "Composição do portfólio", que começa 10px abaixo do card. Como a caixa não
    tem `overflow: hidden`, o estouro apareceria inteiro na projeção.

    O fator é a razão entre a largura real e os 170px de referência, limitado a
    1 para que cinco cards ou menos continuem pixel a pixel como o deck
    aprovado — só o caso novo encolhe.
  */
  const cardScale = Math.min(1, cardW / 170);
  /**
   * Altura reservada à manchete do card e onde o texto de apoio começa.
   *
   * O 236 fixo de antes valia para cinco situações, em que a manchete cabia em
   * UMA linha. Com seis, o card estreita e "Em uso pelo negócio" quebra em duas
   * — e o apoio, ancorado no 236, era desenhado por cima dela. Reservar duas
   * linhas mantém TODOS os cards alinhados entre si, que é o que faz a fileira
   * ler como fileira; alinhar cada um pela sua própria altura deixaria os
   * parágrafos em degraus.
   */
  const tituloH = 14.3 * cardScale * 1.15 * 2;
  const apoioTop = cardScale < 1 ? 210 + tituloH + 6 : 236;
  /**
   * Com seis situacoes o card precisa de mais altura, e ela vem de CIMA: entre
   * o bloco do numero-heroi e a fileira sobra uma faixa vazia, enquanto embaixo
   * o rotulo "Composicao do portfolio" fica a 10px da base do card. Subir 40px
   * e crescer 40 mantem a base exatamente onde estava.
   *
   * So vale quando os cards encolhem — com cinco ou menos o slide continua
   * pixel a pixel igual ao material aprovado.
   */
  const cardTop = cardScale < 1 ? 370 : 410;
  const cardH = cardScale < 1 ? 340 : 300;

  return (
    <>
      <DeckRail
        kicker="— Índice geral"
        title="Visão"
        titleSize={60}
        footer={`Masterboi  ·  ${years}`}
      >
        <DeckLabel top={444} left={DECK_GEO.railPad} color={DECK.redLabel}>
          Portfólio {years}
        </DeckLabel>
        <div
          style={{
            position: 'absolute',
            left: DECK_GEO.railPad,
            top: 466,
            width: DECK_GEO.railW - DECK_GEO.railPad - 40,
            fontSize: 15,
            lineHeight: 1.4,
            color: '#fff',
          }}
        >
          Plataformas em desenvolvimento e entregues {phrase} pelo time de Dados, Inovação e IA.
        </div>

        {/*
          Gasto mensal do portfólio, no trilho e não na coluna branca: a coluna
          já carrega o número-herói, a fileira de situações e a barra de
          composição, e com seis situações os cards sobem para 370 — não sobra
          faixa vertical para um segundo número grande sem espremer algo. Aqui
          embaixo, entre a frase de abertura (termina ~550) e o rodapé (774),
          havia vermelho vazio.
        */}
        <DeckLabel top={600} left={DECK_GEO.railPad} color={DECK.redLabel}>
          Gasto mensal na nuvem
        </DeckLabel>
        <div
          style={{
            position: 'absolute',
            left: DECK_GEO.railPad,
            top: 620,
            fontSize: 46,
            fontWeight: 800,
            lineHeight: 1.1,
            letterSpacing: '-0.03em',
            color: '#fff',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {/* "—" quando o custo ou a cotação não chegaram: ver `gastoDaNuvem`. */}
          {formatBrlBR(nuvem.totalBrl) || '—'}
        </div>
        <div
          style={{
            position: 'absolute',
            left: DECK_GEO.railPad,
            top: 678,
            width: DECK_GEO.railW - DECK_GEO.railPad - 40,
            fontSize: 13,
            lineHeight: 1.35,
            color: DECK.redLabel,
          }}
        >
          {nuvem.plataformas === 1
            ? '1 plataforma hospedada · as demais rodam em servidor próprio, sem custo mensal.'
            : `${nuvem.plataformas} plataformas hospedadas · as demais rodam em servidor próprio, sem custo mensal.`}
        </div>
      </DeckRail>

      <DeckLabel top={92} left={contentX + 17} color={DECK.inkMuted}>
        Total de plataformas
      </DeckLabel>

      <div
        style={{
          position: 'absolute',
          left: contentX + 10,
          top: 132,
          fontSize: 200,
          fontWeight: 800,
          lineHeight: 1,
          letterSpacing: '-0.05em',
          color: DECK.red,
        }}
      >
        {apps.length}
      </div>
      <div
        style={{
          position: 'absolute',
          left: 768,
          top: 188,
          width: 620,
          fontSize: 21.8,
          lineHeight: 1.35,
          color: DECK.ink,
        }}
      >
        {apps.length === 1 ? 'aplicação entregando' : 'aplicações entregando'} valor para o Grupo
        Masterboi.
      </div>

      {counts.map((s, i) => (
        <div
          key={s.key}
          style={{
            position: 'absolute',
            left: contentX + 10 + i * (cardW + gap),
            top: cardTop,
            width: cardW,
            height: cardH,
            background: DECK.panel,
          }}
        >
          <div style={{ height: 7.5, background: s.color }} />
          <DeckStatusPill
            label={s.label}
            color={s.color}
            left={24}
            top={30}
            height={31.5 * cardScale}
            fontSize={11.3 * cardScale}
          />
          <div
            style={{
              position: 'absolute',
              left: 22,
              top: 46,
              fontSize: 120 * cardScale,
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: '-0.05em',
              color: s.color,
            }}
          >
            {s.count}
          </div>
          {/* A manchete RESERVA duas linhas quando o card encolhe. Com cinco
              situações ela cabia em uma só e o apoio podia começar num 236
              fixo; com seis, "Em uso pelo negócio" quebra em duas e o apoio
              era desenhado POR CIMA dela — visto na projeção, com as duas
              frases embaralhadas no primeiro card. Em escala 1 nada muda: o
              deck de cinco continua saindo pixel a pixel como o aprovado. */}
          <div
            style={{
              position: 'absolute',
              left: 24,
              top: 210,
              width: cardW - 48,
              height: cardScale < 1 ? tituloH : undefined,
              fontSize: 14.3 * cardScale,
              lineHeight: 1.15,
              fontWeight: 700,
              color: DECK.ink,
            }}
          >
            {s.headline}
          </div>
          <div
            style={{
              position: 'absolute',
              left: 24,
              top: apoioTop,
              width: cardW - 48,
              // O card não tinha corte nenhum: o apoio transbordava a base e ia
              // parar em cima do rótulo "Composição do portfólio". Fica limitado
              // ao que sobra — e o card cresceu 40px justamente para o texto
              // caber inteiro em vez de ser cortado no meio da frase.
              maxHeight: cardH - apoioTop - 12,
              overflow: 'hidden',
              fontSize: 12 * cardScale,
              lineHeight: 1.28,
              color: DECK.inkMuted,
            }}
          >
            {s.blurb}
          </div>
        </div>
      ))}

      {counts.length === 0 && (
        <div
          style={{
            position: 'absolute',
            left: contentX + 10,
            top: 410,
            width: contentW,
            height: 300,
            border: `1px dashed #D4D4D4`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            color: DECK.inkMuted,
          }}
        >
          Nenhuma plataforma corresponde aos filtros escolhidos.
        </div>
      )}

      <DeckLabel top={720} left={contentX + 17} color={DECK.inkMuted}>
        Composição do portfólio
      </DeckLabel>
      <div
        style={{
          position: 'absolute',
          left: contentX + 10,
          top: 740,
          width: contentW,
          height: 13.5,
          display: 'flex',
          background: DECK.panel,
        }}
      >
        {counts.map((s) => (
          <div key={s.key} style={{ width: `${s.share}%`, background: s.color }} />
        ))}
      </div>

      <div style={{ position: 'absolute', right: 50, top: footerY - 8 }}>
        <DeckLogo tone="red" size={22} />
      </div>
    </>
  );
}

const SHOT_SHADOW = '0 18px 44px rgba(0,0,0,0.20)';

/**
 * Substituto da captura quando a aplicação não tem imagem cadastrada: janela
 * falsa com o avatar da marca. Diz a verdade ("sem captura cadastrada") em vez
 * de fingir uma tela — e serve de lembrete para quem mantém o inventário, já
 * que o deck é o lugar onde a falta aparece.
 */
function SlidePlaceholder({
  app,
  width,
  height,
}: Readonly<{ app: App; width: number; height: number }>) {
  return (
    <div
      style={{
        width,
        height,
        borderRadius: 6,
        overflow: 'hidden',
        background: '#fff',
        border: '1px solid #E5E5E5',
        boxShadow: SHOT_SHADOW,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          height: 34,
          flexShrink: 0,
          background: '#F0F0F0',
          borderBottom: '1px solid #E2E2E2',
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: '0 14px',
        }}
      >
        {['#FF5F57', '#FEBC2E', '#28C840'].map((c) => (
          <span key={c} style={{ width: 10, height: 10, borderRadius: '50%', background: c }} />
        ))}
        <div
          style={{
            marginLeft: 14,
            height: 18,
            width: 300,
            borderRadius: 9,
            background: '#E4E4E4',
          }}
        />
      </div>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 18,
          background: `linear-gradient(150deg, ${app.iconBg}1a 0%, ${app.iconBg}08 55%, #FAFAFA 100%)`,
        }}
      >
        <AppPhoto app={app} size={96} dark={false} />
        <div style={{ fontSize: 26, fontWeight: 700, color: DECK.ink, letterSpacing: '-0.015em' }}>
          {app.name}
        </div>
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: '#A3A3A3',
          }}
        >
          Sem captura cadastrada
        </div>
      </div>
    </div>
  );
}

/** Botão redondo das setas do carrossel — igual dos dois lados, só espelhado. */
function ShotArrow({
  lado,
  onClick,
}: Readonly<{ lado: 'anterior' | 'proxima'; onClick: () => void }>) {
  const anterior = lado === 'anterior';
  return (
    <button
      type="button"
      className="deck-carousel-ui"
      onClick={onClick}
      title={anterior ? 'Tela anterior (↑)' : 'Próxima tela (↓)'}
      aria-label={anterior ? 'Tela anterior' : 'Próxima tela'}
      style={{
        position: 'absolute',
        top: '50%',
        [anterior ? 'left' : 'right']: 2,
        transform: 'translateY(-50%)',
        width: 46,
        height: 46,
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.34)',
        background: 'rgba(17,17,20,0.62)',
        color: '#fff',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
        // Acima da área clicável que cobre a captura inteira (abaixo).
        zIndex: 2,
      }}
    >
      {anterior ? <ChevronBack size={18} /> : <IconChevronR size={18} />}
    </button>
  );
}

/**
 * Controles do carrossel: área clicável sobre a captura, setas e indicador de
 * posição.
 *
 * As setas e o contador existem porque numa projeção NINGUÉM descobre clique
 * escondido — o apresentador precisa ver que há mais telas, e a plateia precisa
 * saber que está na 2 de 5. Tudo marcado com `deck-carousel-ui`, que o
 * `@media print` apaga: é controle de tela, não conteúdo do slide.
 */
function ShotCarouselUI({
  atual,
  total,
  onStep,
}: Readonly<{ atual: number; total: number; onStep: (delta: number) => void }>) {
  return (
    <>
      {/* A captura inteira avança — foi o pedido ("clicar para avançar a
          imagem como se fosse um carrossel"). Botão transparente por cima em vez
          de `onClick` na imagem: dá foco por teclado e rótulo acessível sem
          transformar cada <img> em elemento interativo. */}
      <button
        type="button"
        className="deck-carousel-ui"
        onClick={() => onStep(1)}
        title="Clique para avançar a tela (↓ e ↑ no teclado)"
        aria-label={`Próxima tela · ${atual + 1} de ${total}`}
        style={{
          position: 'absolute',
          inset: 0,
          padding: 0,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
        }}
      />
      <ShotArrow lado="anterior" onClick={() => onStep(-1)} />
      <ShotArrow lado="proxima" onClick={() => onStep(1)} />
      <div
        className="deck-carousel-ui"
        style={{
          position: 'absolute',
          left: '50%',
          bottom: 2,
          transform: 'translateX(-50%)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          padding: '7px 13px',
          borderRadius: 999,
          background: 'rgba(17,17,20,0.72)',
          border: '1px solid rgba(255,255,255,0.16)',
          color: '#fff',
          zIndex: 2,
        }}
      >
        {/* Bolinhas só até 10 telas: acima disso viram uma régua ilegível a
            oito metros de distância e o contador já diz tudo. */}
        {total <= 10 && (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            {Array.from({ length: total }, (_, i) => (
              <span
                key={`dot-${i + 1}`}
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: i === atual ? '#fff' : 'rgba(255,255,255,0.38)',
                }}
              />
            ))}
          </span>
        )}
        <span style={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {atual + 1}
          <span style={{ color: 'rgba(255,255,255,0.5)' }}> / {total}</span>
        </span>
      </div>
    </>
  );
}

/**
 * Captura da aplicação no slide — e, com mais de uma imagem cadastrada, o
 * CARROSSEL que o apresentador usa para ir mostrando as telas.
 *
 * `objectFit: contain` (e não `cover` como no card do catálogo) porque aqui a
 * imagem é o argumento da apresentação: cortar a lateral de um dashboard
 * esconde justamente o que se quer mostrar. Imagem ausente — ou que falha ao
 * carregar — cai no placeholder, então o slide nunca mostra ícone de imagem
 * quebrada na frente da diretoria.
 *
 * TODAS as telas ficam no DOM (só a atual visível, as outras em `display:none`,
 * que o navegador baixa do mesmo jeito): trocar de tela numa projeção não pode
 * esperar download. Com UMA imagem só, nenhum controle aparece — seta que não
 * leva a lugar nenhum é pior que seta nenhuma.
 *
 * A posição vem de FORA (`pos`) porque quem manda nela é o teclado da
 * apresentação, que vive na tela, e porque é assim que ela volta à capa sozinha
 * quando o apresentador troca de slide.
 */
function SlideShot({
  app,
  width,
  height,
  pos,
  onStep,
}: Readonly<{
  app: App;
  width: number;
  height: number;
  /** Posição pedida; pode ser negativa ou maior que a lista — damos a volta. */
  pos: number;
  /** Avança (+1) ou volta (−1) uma tela. */
  onStep: (delta: number) => void;
}>) {
  // Guardamos as URLs que FALHARAM (não um boolean): com galeria, uma tela
  // quebrada não pode derrubar as outras — ela sai da roda e o resto continua.
  const [falhas, setFalhas] = useState<string[]>([]);
  const telas = applicationImageUrls(app, 1280).filter((u) => !falhas.includes(u));

  if (telas.length === 0) {
    return <SlidePlaceholder app={app} width={Math.min(880, width - 50)} height={height - 40} />;
  }

  // Volta ao começo depois da última (e ao fim antes da primeira): numa
  // projeção, carrossel que trava no fim é o apresentador clicando num botão
  // morto enquanto a sala olha.
  const atual = ((pos % telas.length) + telas.length) % telas.length;

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {telas.map((url, i) => (
        <div
          key={url}
          // `deck-shot-capa` na primeira: é ela que o PDF imprime, esteja o
          // carrossel onde estiver (ver o @media print do DECK_CSS).
          className={i === 0 ? 'deck-shot-frame deck-shot-capa' : 'deck-shot-frame'}
          style={{
            display: i === atual ? 'flex' : 'none',
            maxWidth: '100%',
            maxHeight: '100%',
            background: '#fff',
            padding: 10,
            borderRadius: 4,
            boxShadow: SHOT_SHADOW,
          }}
        >
          <img
            src={url}
            alt={
              telas.length > 1
                ? `Tela ${i + 1} de ${telas.length} de ${app.name}`
                : `Captura de ${app.name}`
            }
            onError={() => setFalhas((f) => (f.includes(url) ? f : [...f, url]))}
            style={{
              display: 'block',
              maxWidth: width - 40,
              maxHeight: height - 40,
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </div>
      ))}
      {telas.length > 1 && <ShotCarouselUI atual={atual} total={telas.length} onStep={onStep} />}
    </div>
  );
}

/**
 * Página que só existe no PAPEL: uma por tela adicional da galeria.
 *
 * Papel não tem carrossel. Sem isto o PDF sairia com a capa e mais nada — as
 * telas que o apresentador mostra clicando não existiriam no documento que
 * circula depois da reunião, e é justamente ele que a diretoria relê.
 *
 * É IRMÃ do slide, não filha: `.deck-slide` tem 810px de altura com
 * `overflow:hidden`, então uma página aninhada sairia recortada na impressão. E
 * a classe é própria (`deck-print-slide`) para não entrar na contagem de
 * slides: a navegação conta o plano em JS, e o índice continua falando de
 * plataformas, não de telas.
 *
 * Como não há bloco cinza aqui, a captura ganha a altura toda até o rodapé — a
 * tela impressa sai MAIOR que no slide da plataforma, que é o que se quer de
 * uma página dedicada a ela.
 */
function ShotPrintSlide({
  app,
  index,
  years,
  url,
  ordem,
  total,
}: Readonly<{
  app: App;
  index: number;
  years: string;
  url: string;
  /** Posição desta tela na galeria (a capa é a 1). */
  ordem: number;
  total: number;
}>) {
  const { railPad, contentX, contentW, shotY, footerY } = DECK_GEO;
  const altura = footerY - shotY - 20;
  // Tela que não carrega não ganha página: melhor uma a menos no PDF do que uma
  // folha inteira com ícone de imagem quebrada circulando pela diretoria.
  const [falhou, setFalhou] = useState(false);
  if (falhou) return null;

  return (
    <div className="deck-print-slide">
      <DeckRail
        kicker={`— Plataforma nº ${deckSlideNumber(index)}`}
        title={app.name}
        titleSize={deckTitleSize(app.name)}
        footer={`Masterboi  ·  ${years}`}
        watermark={deckSlideNumber(index)}
      >
        <DeckLabel top={444} left={railPad} color={DECK.redLabel}>
          Tela da aplicação
        </DeckLabel>
        <div
          style={{
            position: 'absolute',
            left: railPad,
            top: 462,
            fontSize: 24,
            fontWeight: 700,
            color: '#fff',
          }}
        >
          {ordem} de {total}
        </div>
      </DeckRail>

      <div
        style={{
          position: 'absolute',
          left: contentX,
          top: shotY,
          width: contentW,
          height: altura,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            background: '#fff',
            padding: 10,
            borderRadius: 4,
            boxShadow: SHOT_SHADOW,
            display: 'flex',
          }}
        >
          {/* Sem `loading="lazy"`: a página está oculta na tela, e o lazy
              deixaria a imagem para depois — o PDF sairia com o buraco que o
              `waitForAssets` existe para evitar. */}
          <img
            src={url}
            alt={`Tela ${ordem} de ${total} de ${app.name}`}
            onError={() => setFalhou(true)}
            style={{
              display: 'block',
              maxWidth: contentW - 40,
              maxHeight: altura - 40,
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </div>
      </div>

      <div style={{ position: 'absolute', right: 50, top: footerY - 8 }}>
        <DeckLogo tone="red" size={22} />
      </div>
    </div>
  );
}

/**
 * Um campo da pilha de identidade do painel vermelho, já medido.
 *
 * `lines` não é um teto de estilo: é a altura que o campo RESERVOU no
 * orçamento, e é ela que empurra (ou não) o status e os carimbos para baixo.
 */
interface RailCampo {
  label: string;
  valor: string;
  size: number;
  lines: number;
  /** Número: ganha `tabular-nums` para as horas não dançarem entre slides. */
  numero?: boolean;
  y: number;
}

interface RailLayout {
  campos: RailCampo[];
  statusY: number;
  pillH: number;
  metaLabelY: number;
  metaValueY: number;
  /** Segunda linha de carimbos ("Acessos no mês"): rótulo e valor. */
  acessosLabelY: number;
  acessosValueY: number;
}

/**
 * Onde cada peça do painel vermelho cai, para ESTA aplicação.
 *
 * A pilha de identidade é a ordem que a diretoria pediu — área solicitante,
 * dono do projeto, responsável e esforço —, e os quatro campos são OPCIONAIS no
 * cadastro: 42 das 44 aplicações não têm nenhum campo de projeto e só entram
 * com o responsável. Campo vazio simplesmente não existe na pilha; não vira
 * rótulo órfão, nem "—", nem buraco entre dois campos preenchidos.
 *
 * Como a altura da pilha varia, o status e os carimbos não podem ser coordenada
 * fixa nem coordenada livre:
 *
 *  · FIXA quebraria — com os quatro campos e um nome de setor de duas linhas a
 *    pilha desceria por cima da pílula de status;
 *  · LIVRE piscaria — o "Custo mensal" subiria e desceria a cada avanço de
 *    slide, e numa projeção isso salta aos olhos mais do que o dado.
 *
 * Daí o MÍNIMO: os dois blocos ficam onde sempre ficaram (524 e 614) e só
 * descem quando a pilha realmente chega até eles. Nas 42 aplicações sem projeto
 * o painel sai pixel a pixel igual ao de antes.
 *
 * E como último recurso o orçamento corta LINHA, não bloco: se a pilha inteira
 * não couber em `DECK_RAIL_STACK.budget`, os campos perdem a segunda linha do
 * último para o primeiro (a área é a que mais precisa dela, então é a última a
 * ceder). Um nome de pessoa com reticências é um mal muito menor do que a linha
 * de custo mensal impressa por cima da assinatura Masterboi.
 */
function railLayout(app: App): RailLayout {
  const S = DECK_RAIL_STACK;
  const horas = deckEsforcoHoras(app);
  const brutos = [
    { label: 'Área solicitante', valor: app.projetoArea },
    { label: 'Dono do projeto', valor: app.projetoDono },
    { label: 'Responsável', valor: app.owner },
    { label: 'Esforço do projeto', valor: horas, numero: true },
  ]
    .filter((c) => preenchido(c.valor))
    .map((c, i) => ({
      label: c.label,
      valor: (c.valor as string).trim(),
      // O PRIMEIRO campo faz o papel de manchete do painel — é ele que ocupa o
      // lugar do antigo "Time responsável" em corpo grande. Quem vem depois é
      // apoio, e o total de horas tem corpo próprio por ser número.
      size: c.numero ? S.esforcoSize : i === 0 ? S.leadSize : S.size,
      numero: c.numero,
    }));

  const alturaDe = (size: number, lines: number) =>
    S.labelH + lines * Math.round(size * S.lineRatio);
  const altura = (ls: readonly number[]) =>
    ls.reduce((soma, l, i) => soma + alturaDe(brutos[i].size, l), 0) +
    Math.max(0, ls.length - 1) * S.gap;

  // Horas nunca quebram linha ("236 h"); os textos pedem o que precisarem, até
  // o teto de duas — na terceira o painel deixa de ser painel e vira ficha.
  const lines = brutos.map((c) =>
    c.numero
      ? 1
      : Math.min(S.maxLines, linhasDe(c.valor, charsPorLinha(S.width, c.size, S.charRatio))),
  );
  for (let i = lines.length - 1; i >= 0 && altura(lines) > S.budget; i--) lines[i] = 1;

  let y = S.top;
  const campos: RailCampo[] = brutos.map((c, i) => {
    const campo = { ...c, lines: lines[i], y };
    y += alturaDe(c.size, lines[i]) + S.gap;
    return campo;
  });

  const fimDaPilha = campos.length > 0 ? y - S.gap : S.top;
  const statusY = Math.max(S.statusMinY, fimDaPilha + S.blockGap);
  // A pílula cheia só cabe quando a pilha não empurrou nada; empurrada, ela
  // encolhe para o vão até os carimbos não sumir.
  const pillH = statusY === S.statusMinY ? S.pillTall : S.pillShort;
  const metaLabelY = Math.max(DECK_RAIL_META.labelY, statusY + S.statusLabelH + pillH + S.blockGap);
  const metaValueY = metaLabelY + DECK_RAIL_META.valueH;
  // A linha de acessos vem PRESA à primeira (não tem mínimo próprio): as duas
  // sobem e descem juntas, e o vão entre elas é o mesmo dos outros blocos.
  const acessosLabelY = metaValueY + DECK_RAIL_META.valueH + DECK_RAIL_META.row2Gap;
  const acessosValueY = acessosLabelY + DECK_RAIL_META.valueH;
  return {
    campos,
    statusY,
    pillH,
    metaLabelY,
    metaValueY,
    acessosLabelY,
    acessosValueY,
  };
}

/**
 * MIOLO do painel vermelho: identidade, status e os dois carimbos.
 *
 * A ordem dos campos é a que o dono do produto ditou — área solicitante, dono
 * do projeto, responsável e esforço —, e o que saiu daqui foi o "Time
 * responsável" (`app.team`): as 44 aplicações cadastradas têm
 * `team = "Desenvolvimento"`, então o campo gastava uma linha do painel para
 * repetir a mesma palavra em todo slide do deck. Quem apresenta procura no
 * painel a PESSOA (`app.owner`), que antes só aparecia na régua de metadados do
 * card cinza, longe do resto da identidade da aplicação.
 */
function RailFacts({ app, custoBrl }: Readonly<{ app: App; custoBrl: number | null }>) {
  const { railPad } = DECK_GEO;
  const S = DECK_RAIL_STACK;
  const status = deckStatusOf(app);
  const updated = formatDateBR(app.lastUpdate);
  const acessos = deckAcessos(app);
  const layout = railLayout(app);

  return (
    <>
      {layout.campos.map((campo) => (
        <Fragment key={campo.label}>
          <DeckLabel top={campo.y} left={railPad} color={DECK.redLabel}>
            {campo.label}
          </DeckLabel>
          <div
            style={{
              position: 'absolute',
              left: railPad,
              top: campo.y + S.labelH,
              width: S.width,
              fontSize: campo.size,
              fontWeight: campo.numero || campo.size === S.leadSize ? 700 : 600,
              lineHeight: S.lineRatio,
              color: '#fff',
              fontVariantNumeric: campo.numero ? 'tabular-nums' : undefined,
              // O corte é o que o layout RESERVOU (ver `railLayout`): sem ele um
              // nome de setor de 200 caracteres desceria por cima do status. O
              // `title` guarda o texto inteiro para quem apontar na tela.
              display: '-webkit-box',
              WebkitLineClamp: campo.lines,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              overflowWrap: 'break-word',
            }}
            title={campo.valor}
          >
            {campo.valor}
          </div>
        </Fragment>
      ))}

      <DeckLabel top={layout.statusY} left={railPad} color={DECK.redLabel}>
        Status atual
      </DeckLabel>
      <DeckStatusPill
        label={status.label}
        color={status.color}
        left={50}
        top={layout.statusY + S.statusLabelH}
        width={280}
        height={layout.pillH}
        fontSize={layout.pillH === S.pillTall ? 14.3 : 12.5}
      />

      {/* Os dois CARIMBOS lado a lado (ver `DECK_RAIL_META`). Emparelhá-los é o
          que abre espaço para a pilha de identidade acima — e os valores são
          curtos demais para justificar uma linha inteira cada um.

          O rótulo do custo NOMEIA A FONTE de propósito: "R$ 0,00" sozinho leria
          como "esta aplicação não custa nada", que é falso para on-premise — ela
          custa, só não no Railway. Com "· Railway" o zero diz a verdade exata. */}
      {updated && (
        <>
          <DeckLabel top={layout.metaLabelY} left={railPad} color={DECK.redLabel}>
            Última atualização
          </DeckLabel>
          <div
            style={{
              position: 'absolute',
              left: railPad,
              top: layout.metaValueY,
              width: DECK_RAIL_META.col1W,
              fontSize: 15,
              fontWeight: 600,
              lineHeight: 1.2,
              color: '#fff',
            }}
          >
            {updated}
          </div>
        </>
      )}
      <DeckLabel top={layout.metaLabelY} left={DECK_RAIL_META.col2X} color={DECK.redLabel}>
        Custo mensal · Railway
      </DeckLabel>
      <div
        style={{
          position: 'absolute',
          left: DECK_RAIL_META.col2X,
          top: layout.metaValueY,
          width: DECK_RAIL_META.col2W,
          fontSize: 15,
          fontWeight: 600,
          lineHeight: 1.2,
          color: '#fff',
          // A coluna tem 178px e o valor em reais é MAIS CURTO que o de antes
          // ("R$ 9,70" contra "US$ 9,70"), então a conversão não aperta nada. O
          // `nowrap` é cinto de segurança para o dia em que um projeto passar
          // dos milhares: em vez de cair para uma segunda linha e invadir a
          // marca d'água (a pilha termina em 650 e ela começa em 666), o texto
          // sai pela direita, onde há painel vazio. Nunca empurra o layout.
          whiteSpace: 'nowrap',
          // Números em colunas alinhadas de slide para slide — mesma regra dos
          // demais valores numéricos do painel.
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {custoBrl === null ? '—' : formatBrlBR(custoBrl)}
      </div>

      {/* SEGUNDA linha de carimbos: dias do mês com acesso, lidos do Clerk da
          aplicação. Só na coluna esquerda — a direita, daqui para baixo, é a
          marca d'água (ver `DECK_RAIL_META`). "12/22" já carrega a janela no
          próprio formato, então o rótulo só nomeia o quê. */}
      <DeckLabel top={layout.acessosLabelY} left={railPad} color={DECK.redLabel}>
        Acessos no mês
      </DeckLabel>
      <div
        style={{
          position: 'absolute',
          left: railPad,
          top: layout.acessosValueY,
          width: DECK_RAIL_META.col1W,
          fontSize: 15,
          fontWeight: 600,
          lineHeight: 1.2,
          color: '#fff',
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        }}
        title={acessos.titulo}
      >
        {acessos.valor}
      </div>
    </>
  );
}

// memo(): trocar filtro/slide re-renderiza o ScreenPresentation inteiro; sem o
// memo, TODOS os slides de plataforma (um por app do portfólio) re-renderizavam
// a cada clique de pílula — era o "engasgo" percebido ao selecionar filtros.
// `shotPos` só chega com valor no slide ATIVO (nos demais é 0 e não muda), senão
// avançar o carrossel re-renderizaria os 44 slides de uma vez.
/**
 * Card cinza "Sobre a plataforma": a descrição da aplicação e, abaixo dela, o
 * retorno esperado do projeto.
 *
 * Saiu daqui a régua de metadados (Tipo, Responsável, Criticidade e Stack). Ela
 * comia a faixa de 694 a 740 do card para repetir, em corpo 13, dados que o
 * painel vermelho já dá na lateral — e o "Responsável" dela agora mora lá, com
 * o resto da identidade da aplicação.
 *
 * O bloco do retorno é ANCORADO na base do card e a descrição fica com o que
 * sobra em cima: o rótulo "Retorno esperado" cai sempre na mesma altura, em
 * toda plataforma que o tenha, em vez de flutuar conforme o tamanho da
 * descrição — numa sequência de slides, rótulo que sobe e desce é o que o olho
 * persegue. Sem retorno cadastrado o card NÃO ganha rótulo órfão: a descrição
 * herda o card inteiro e passa das 4 linhas de antes para 6.
 */
function AboutCard({ app }: Readonly<{ app: App }>) {
  const { contentX, contentW, aboutY, aboutH } = DECK_GEO;
  const A = DECK_ABOUT;
  const left = contentX + A.padX;
  const retorno = app.projetoRetorno?.trim() ?? '';
  const temRetorno = retorno !== '';

  const descSize = app.description.length > 300 ? 13 : 14.2;
  const descLH = descSize * A.descLineRatio;
  const retornoLH = A.retornoSize * A.retornoLineRatio;
  const blocoRetorno = A.labelH + A.retornoLines * retornoLH;
  const retornoY = A.bottom - blocoRetorno;
  const descBottom = temRetorno ? retornoY - A.gap : A.bottom;
  const descLines = Math.max(1, Math.floor((descBottom - A.descY) / descLH));

  // Sobrou texto? O cadastro aceita 2000 caracteres, então isto não é hipótese:
  // é o estado do banco hoje. A contagem PESA as maiúsculas (ver
  // `DECK_ABOUT.maiusculaPeso`) porque o limite foi medido em prosa, e um
  // retorno escrito em caixa alta estoura o card com um quinto menos de letras
  // — sem o peso, justamente ele cortaria em silêncio.
  const sobra = temRetorno && pesoDeTexto(retorno) > DECK_RETORNO_MAX;

  return (
    <>
      <div
        style={{
          position: 'absolute',
          left: contentX,
          top: aboutY,
          width: contentW,
          height: aboutH,
          background: DECK.panel,
          borderLeft: `4px solid ${DECK.red}`,
        }}
      />
      <DeckLabel top={A.labelY} left={left} color={DECK.red}>
        Sobre a plataforma
      </DeckLabel>
      <div
        style={{
          position: 'absolute',
          left,
          top: A.descY,
          width: A.width,
          fontSize: descSize,
          lineHeight: A.descLineRatio,
          color: DECK.ink,
          display: '-webkit-box',
          WebkitLineClamp: descLines,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
        title={app.description}
      >
        {app.description}
      </div>

      {temRetorno && (
        <>
          {/* O aviso de estouro vai na LINHA DO RÓTULO, não abaixo do texto:
              abaixo não há pixel livre (o texto termina na borda do card), e
              cortar em silêncio é o desfecho que não pode acontecer — o
              apresentador descobriria ao vivo que a frase acabou no meio. */}
          <div
            style={{
              position: 'absolute',
              left,
              top: retornoY,
              width: A.width,
              display: 'flex',
              alignItems: 'baseline',
              gap: 9,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.11em',
              textTransform: 'uppercase',
              lineHeight: 1,
            }}
          >
            <span style={{ color: DECK.red }}>Retorno esperado</span>
            {sobra && (
              <span style={{ color: DECK.inkMuted, letterSpacing: '0.06em' }}>
                · texto completo na ficha da aplicação
              </span>
            )}
          </div>
          <div
            style={{
              position: 'absolute',
              left,
              top: retornoY + A.labelH,
              width: A.width,
              fontSize: A.retornoSize,
              lineHeight: A.retornoLineRatio,
              color: DECK.ink,
              display: '-webkit-box',
              WebkitLineClamp: A.retornoLines,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
            title={retorno}
          >
            {retorno}
          </div>
        </>
      )}
    </>
  );
}

const PlatformSlide = memo(function PlatformSlide({
  app,
  index,
  years,
  custoBrl,
  shotPos,
  onShotStep,
}: Readonly<{
  app: App;
  index: number;
  years: string;
  /** Tela atual do carrossel da captura. */
  shotPos: number;
  onShotStep: (delta: number) => void;
  /**
   * Custo mensal estimado no Railway, JÁ EM REAIS. `0` é resposta LEGÍTIMA —
   * aplicação que roda on-premise não consome nuvem —, e `null` é "ainda não
   * sei" (custo carregando, consulta falhou ou não há cotação confiável para
   * converter). Os dois casos precisam de símbolos diferentes: R$ 0,00 afirma,
   * "—" se cala. Sem essa distinção, um erro de rede projetaria "custo zero" em
   * TODOS os slides.
   *
   * A conversão chega pronta de cima porque a cotação é UMA para o deck inteiro
   * (ver `ScreenPresentation`) — não faria sentido cada slide consultá-la.
   */
  custoBrl: number | null;
}>) {
  const { contentX, contentW, shotY, shotH, footerY } = DECK_GEO;

  return (
    <>
      <DeckRail
        kicker={`— Plataforma nº ${deckSlideNumber(index)}`}
        title={app.name}
        titleSize={deckTitleSize(app.name)}
        footer={`Masterboi  ·  ${years}`}
        watermark={deckSlideNumber(index)}
      >
        <RailFacts app={app} custoBrl={custoBrl} />
      </DeckRail>

      {/* Captura da aplicação — imagem cadastrada ou o placeholder sintético. */}
      <div
        style={{
          position: 'absolute',
          left: contentX,
          top: shotY,
          width: contentW,
          height: shotH,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <SlideShot app={app} width={contentW} height={shotH} pos={shotPos} onStep={onShotStep} />
      </div>

      <AboutCard app={app} />

      <div style={{ position: 'absolute', right: 50, top: footerY - 8 }}>
        <DeckLogo tone="red" size={22} />
      </div>
    </>
  );
});

function ClosingSlide({ total, years }: Readonly<{ total: number; years: string }>) {
  return (
    <>
      <DeckRedBackdrop />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 26,
        }}
      >
        <DeckLogo tone="white" size={44} />
        <div style={{ width: 240, height: 4, background: '#fff', opacity: 0.9 }} />
        <div
          style={{
            fontSize: 30,
            fontWeight: 400,
            color: '#fff',
            letterSpacing: '-0.01em',
            textAlign: 'center',
          }}
        >
          Dados, IA e Inovação · {years}
        </div>
        <div
          style={{
            fontSize: 16,
            color: 'rgba(255,255,255,0.82)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontWeight: 700,
          }}
        >
          {total} {total === 1 ? 'plataforma apresentada' : 'plataformas apresentadas'}
        </div>
      </div>
    </>
  );
}

// ── Chrome (só na tela — o `@media print` esconde tudo isto) ────────────────

/**
 * Seta para trás: o mesmo chevron do "Próximo", espelhado por `transform`. O
 * conjunto de ícones não tem uma variante à esquerda, e girar o traço garante
 * que os dois controles fiquem simétricos ao pixel.
 */
function ChevronBack({ size }: Readonly<{ size: number }>) {
  return (
    <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
      <IconChevronR size={size} />
    </span>
  );
}

function ChromeButton({
  children,
  onClick,
  title,
  primary = false,
  disabled = false,
  grow = false,
}: Readonly<{
  children: React.ReactNode;
  onClick: () => void;
  title?: string;
  primary?: boolean;
  disabled?: boolean;
  /**
   * Deixa o botão esticar para ocupar a linha. Só a barra do celular usa: com
   * dois botões por linha o alvo passa de 90px para ~145px, e é com o dedo que
   * se navega o deck no telefone.
   */
  grow?: boolean;
}>) {
  const vp = useViewport();
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      style={{
        // 40px é o alvo mínimo de toque; no ponteiro segue nos 34px de sempre.
        height: byViewport(vp, { mobile: 40, desktop: 34 }),
        flex: grow ? '1 1 auto' : undefined,
        // Sem efeito quando o botão é do tamanho do conteúdo (o caso do
        // desktop); centraliza o rótulo quando ele estica com `grow`.
        justifyContent: 'center',
        padding: '0 14px',
        borderRadius: 7,
        border: primary ? 'none' : '1px solid rgba(255,255,255,0.18)',
        background: primary ? DECK.red : 'rgba(255,255,255,0.06)',
        color: '#fff',
        fontSize: 12.5,
        fontWeight: 600,
        fontFamily: fontSans,
        cursor: disabled ? 'progress' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

// ── Hub de filtros ──────────────────────────────────────────────────────────

interface FilterOption {
  id: string;
  label: string;
  count: number;
  /** Cor própria da opção (status); sem ela o chip ativo usa o vermelho do deck. */
  color?: string;
}

interface FilterGroup {
  id: string;
  label: string;
  hint: string;
  options: FilterOption[];
  selected: string[];
  toggle: (id: string) => void;
}

/**
 * Chip de opção do hub: apagado quando inativo, cheio na cor do grupo quando
 * ligado (com check). Hover e transição vêm da classe `deck-fpill` — inline
 * style não faz :hover.
 */
function FilterPill({
  option,
  active,
  onClick,
}: Readonly<{
  option: FilterOption;
  active: boolean;
  onClick: () => void;
}>) {
  const vp = useViewport();
  const tint = option.color ?? DECK.red;
  return (
    <button
      type="button"
      className="deck-fpill"
      data-on={active}
      onClick={onClick}
      aria-pressed={active}
      disabled={option.count === 0 && !active}
      // A altura da classe (32px) é alvo de ponteiro; no toque vai a 40px. O
      // inline vence a classe, que é justamente o que a `useViewport` resolve.
      style={
        {
          ['--pill' as string]: tint,
          height: byViewport(vp, { mobile: 40, desktop: 32 }),
        } as CSSProperties
      }
    >
      {active ? (
        <IconCheck size={12} stroke={3} />
      ) : (
        option.color && (
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: option.color,
              flexShrink: 0,
            }}
          />
        )
      )}
      {option.label}
      <span style={{ fontWeight: 700, opacity: active ? 0.85 : 0.45, fontSize: 11 }}>
        {option.count}
      </span>
    </button>
  );
}

interface YearOption {
  year: number;
  count: number;
}

/**
 * Período do deck: pílulas de ano (modo padrão — o ano corrente já vem
 * selecionado) + intervalo de meses "De/Até". Tocar em qualquer select ativa o
 * modo intervalo; clicar numa pílula de ano volta ao modo ano.
 */
function PeriodGroup({
  period,
  setPeriod,
  years,
}: Readonly<{
  period: DeckPeriod;
  setPeriod: (p: DeckPeriod) => void;
  years: YearOption[];
}>) {
  const vp = useViewport();
  // Os selects nascem com 34px na classe `deck-fselect` (alvo de ponteiro); no
  // toque sobem para 40px, senão escolher mês no celular é uma loteria.
  const selectH = byViewport(vp, { mobile: 40, desktop: 34 });
  // Rascunho dos selects: no modo ano, o intervalo equivalente é jan–dez dele,
  // então o primeiro toque no "De/Até" parte do que o usuário já está vendo.
  const range =
    period.mode === 'range'
      ? period
      : {
          mode: 'range' as const,
          start: { year: period.year, month: 1 },
          end: { year: period.year, month: 12 },
        };

  const setPart = (part: 'start' | 'end', patch: Partial<{ year: number; month: number }>) =>
    setPeriod({
      mode: 'range',
      start: part === 'start' ? { ...range.start, ...patch } : range.start,
      end: part === 'end' ? { ...range.end, ...patch } : range.end,
    });

  const yearChoices = years.map((y) => y.year);

  // SelectMenu próprio (não <select>): a lista do nativo é do sistema
  // operacional e saía com cara de HTML padrão dentro do modal escuro do deck.
  const deckTone = {
    bg: '#1d1e24',
    text: '#fff',
    border: 'rgba(255,255,255,0.14)',
    borderActive: 'rgba(255,255,255,0.4)',
    panelBg: '#22232a',
    panelBorder: 'rgba(255,255,255,0.16)',
    optionHover: 'rgba(255,255,255,0.08)',
    optionSelected: '#ff8a86',
    muted: 'rgba(255,255,255,0.55)',
    height: selectH,
    fontSize: 12.5,
    radius: 8,
  };

  const monthSelect = (part: 'start' | 'end') => (
    <SelectMenu
      label={part === 'start' ? 'Mês inicial' : 'Mês final'}
      value={part === 'start' ? range.start.month : range.end.month}
      onChange={(v) => setPart(part, { month: v })}
      options={DECK_MONTHS_PT.map((m, i) => ({ value: i + 1, label: m }))}
      tone={deckTone}
    />
  );

  const yearSelect = (part: 'start' | 'end') => (
    <SelectMenu
      label={part === 'start' ? 'Ano inicial' : 'Ano final'}
      value={part === 'start' ? range.start.year : range.end.year}
      onChange={(v) => setPart(part, { year: v })}
      options={yearChoices.map((y) => ({ value: y, label: String(y) }))}
      tone={deckTone}
    />
  );

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        {years.map((y) => (
          <FilterPill
            key={y.year}
            option={{ id: String(y.year), label: String(y.year), count: y.count }}
            active={period.mode === 'year' && period.year === y.year}
            onClick={() => setPeriod({ mode: 'year', year: y.year })}
          />
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: 'rgba(255,255,255,0.5)' }}>De</span>
        {monthSelect('start')}
        {yearSelect('start')}
        <span style={{ fontSize: 11.5, fontWeight: 600, color: 'rgba(255,255,255,0.5)' }}>até</span>
        {monthSelect('end')}
        {yearSelect('end')}
        {period.mode === 'range' && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              padding: '3px 8px',
              borderRadius: 999,
              background: 'rgba(197,9,5,0.22)',
              color: '#ff8a86',
            }}
          >
            Intervalo ativo
          </span>
        )}
      </div>
    </>
  );
}

/**
 * Popup com tudo que decide o conteúdo do deck. Aplica ao vivo (o rodapé mostra
 * quantas plataformas sobram) porque o resultado é o próprio deck atrás do
 * modal — e o que está filtrado aqui é exatamente o que sai no PDF.
 */
function DeckFilterModal({
  groups,
  query,
  setQuery,
  period,
  setPeriod,
  yearOptions,
  shown,
  total,
  desmarcadas,
  activeCount,
  onClear,
  onClose,
}: Readonly<{
  groups: FilterGroup[];
  query: string;
  setQuery: (v: string) => void;
  period: DeckPeriod;
  setPeriod: (p: DeckPeriod) => void;
  yearOptions: YearOption[];
  shown: number;
  total: number;
  /** Plataformas do recorte que o apresentador desmarcou no lobby. */
  desmarcadas: number;
  activeCount: number;
  onClear: () => void;
  onClose: () => void;
}>) {
  const vp = useViewport();
  // Recuos do popup por faixa. O `@media (max-width:680px)` que fazia isto no
  // <style> precisava de `!important` para vencer o inline — exatamente o que a
  // `useViewport` existe para evitar.
  const gutter = byViewport(vp, { mobile: 14, tablet: 20, desktop: 24 });
  return (
    <div
      className="deck-overlay deck-modal-backdrop"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 30,
        background: 'rgba(5,5,6,0.78)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // No celular a moldura do popup vira 12px: o painel precisa da largura
        // toda para os chips de filtro caberem sem vazar.
        padding: byViewport(vp, { mobile: 12, tablet: 20, desktop: 28 }),
      }}
    >
      {/* Camada de "clicar fora para fechar". É decorativa (o escurecido e o
          blur ficam no contêiner acima) e `aria-hidden` a mantém fora da árvore
          de acessibilidade — mesma escolha do backdrop do InfoPopover. Quem
          navega por teclado fecha com Esc (listener de window) ou pelo botão de
          fechar do cabeçalho; um backdrop focável só criaria uma parada de
          tabulação invisível. */}
      <div aria-hidden="true" onClick={onClose} style={{ position: 'absolute', inset: 0 }} />
      {/* `<dialog open>` (não-modal) em vez de `div role="dialog"`: mesma
          renderização, semântica nativa. As três primeiras declarações do style
          neutralizam o stylesheet do agente (`margin: auto`, `padding: 1em` e
          `color: CanvasText`, que quebraria a herança de cor), e o
          `position: relative` põe o painel acima da camada de clicar fora sem
          mexer no fluxo do flex. */}
      <dialog
        open
        className="deck-modal"
        aria-modal="true"
        aria-label="Filtros da apresentação"
        style={{
          margin: 0,
          padding: 0,
          color: 'inherit',
          position: 'relative',
          zIndex: 1,
          width: 720,
          height: 'auto',
          maxWidth: '100%',
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(180deg, #1a1b20 0%, #141518 100%)',
          border: '1px solid rgba(255,255,255,0.09)',
          borderRadius: 18,
          boxShadow: '0 50px 120px rgba(0,0,0,0.7), 0 0 0 1px rgba(0,0,0,0.4)',
          overflow: 'hidden',
        }}
      >
        {/* Cabeçalho */}
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: byViewport(vp, { mobile: 10, desktop: 14 }),
            padding: `${byViewport(vp, { mobile: 14, desktop: 20 })}px ${gutter}px`,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: `linear-gradient(135deg, ${DECK.red} 0%, #8A0504 100%)`,
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 6px 18px -6px rgba(197,9,5,0.7)',
            }}
          >
            <IconFilter size={17} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>
              Filtros da apresentação
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.48)', marginTop: 2 }}>
              O que você escolher aqui vale para os slides e para o PDF.
            </div>
          </div>
          <button
            type="button"
            className="deck-fclose"
            onClick={onClose}
            title="Fechar (Esc)"
            aria-label="Fechar filtros"
            // 30px na classe é alvo de mouse; no toque vai a 40px — é o botão
            // que fecha o popup em quem não tem tecla Esc.
            style={{
              width: byViewport(vp, { mobile: 40, desktop: 30 }),
              height: byViewport(vp, { mobile: 40, desktop: 30 }),
            }}
          >
            <IconX size={13} />
          </button>
        </div>

        {/* Corpo: rola por dentro (o popup nunca passa de `maxHeight:100%`), que
            é o que faz o painel inteiro caber em 375×812 sem vazar. */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: `4px ${gutter}px ${byViewport(vp, { mobile: 16, desktop: 22 })}px`,
          }}
        >
          {/* 44px de linha no toque: com os 40px da classe, o <input> esticado
              media 38 (a borda de 1px come dois) e ficava 2px abaixo do alvo. */}
          <div
            className="deck-finput"
            style={{
              height: byViewport(vp, { mobile: 44, desktop: 40 }),
              marginBottom: byViewport(vp, { mobile: 16, desktop: 22 }),
            }}
          >
            <span style={{ color: 'rgba(255,255,255,0.4)', display: 'flex' }}>
              <IconSearch size={15} />
            </span>
            <input
              value={query}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
              placeholder="Buscar por nome, área, responsável ou descrição…"
              style={{
                flex: 1,
                // O campo tem 40px de altura, mas o <input> dentro dele media 20:
                // no toque a área de clique era metade do que se vê. `stretch`
                // iguala os dois sem mover o texto (a linha já centraliza).
                alignSelf: byViewport(vp, { mobile: 'stretch', desktop: 'auto' }),
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: '#fff',
                fontSize: 13,
                fontFamily: fontSans,
              }}
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Limpar busca"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  display: 'flex',
                  padding: 0,
                }}
              >
                <IconX size={13} />
              </button>
            )}
          </div>

          {/* Período: ano (padrão) ou intervalo de meses */}
          <div
            style={{
              marginBottom: 18,
              paddingBottom: 18,
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.55)',
                }}
              >
                Período
              </span>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.28)' }}>
                última atualização dentro do período
              </span>
            </div>
            <PeriodGroup period={period} setPeriod={setPeriod} years={yearOptions} />
          </div>

          {groups.map((g, gi) => (
            <div
              key={g.id}
              style={{
                marginBottom: gi === groups.length - 1 ? 0 : 18,
                paddingBottom: gi === groups.length - 1 ? 0 : 18,
                borderBottom:
                  gi === groups.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.55)',
                  }}
                >
                  {g.label}
                </span>
                {g.selected.length > 0 && (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '1px 7px',
                      borderRadius: 999,
                      background: 'rgba(197,9,5,0.22)',
                      color: '#ff8a86',
                    }}
                  >
                    {g.selected.length}
                  </span>
                )}
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.28)' }}>{g.hint}</span>
              </div>
              {g.options.length === 0 ? (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
                  Nada para filtrar aqui.
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {g.options.map((o) => (
                    <FilterPill
                      key={o.id}
                      option={o}
                      active={g.selected.includes(o.id)}
                      onClick={() => g.toggle(o.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Rodapé */}
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            padding: `${byViewport(vp, { mobile: 12, desktop: 16 })}px ${gutter}px`,
            borderTop: '1px solid rgba(255,255,255,0.07)',
            background: 'rgba(0,0,0,0.25)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: shown === 0 ? '#ff8a86' : '#fff',
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {shown}
            </span>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
              de {total} {total === 1 ? 'plataforma' : 'plataformas'} no deck
            </span>
          </div>
          {/* O número acima já desconta quem foi desmarcado no lobby; sem este
              aviso ele vira mistério no meio da apresentação (o chip do filtro
              diz 12, o deck mostra 9). A escolha em si continua sendo do lobby:
              mudar a lista de plataformas com a sala olhando não é gesto de
              apresentação. */}
          {desmarcadas > 0 && (
            <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.4)' }}>
              · {desmarcadas} {desmarcadas === 1 ? 'desmarcada' : 'desmarcadas'} no lobby
            </span>
          )}
          <div style={{ flex: 1 }} />
          {activeCount > 0 && (
            <ChromeButton onClick={onClear} title="Remover todos os filtros" grow={vp.mobile}>
              <IconX size={12} /> Limpar tudo
            </ChromeButton>
          )}
          <ChromeButton onClick={onClose} primary grow={vp.mobile}>
            Ver apresentação
          </ChromeButton>
        </div>
      </dialog>
    </div>
  );
}

interface DeckSlide {
  id: string;
  label: string;
  sublabel?: string;
  color?: string;
}

function IndexOverlay({
  slides,
  current,
  onPick,
  onClose,
}: Readonly<{
  slides: DeckSlide[];
  current: number;
  onPick: (i: number) => void;
  onClose: () => void;
}>) {
  const vp = useViewport();
  const [q, setQ] = useState('');
  const term = q.trim().toLowerCase();
  const hits = slides
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => !term || s.label.toLowerCase().includes(term));

  return (
    <div
      className="deck-overlay"
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(10,10,11,0.82)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        flexDirection: 'column',
        padding: byViewport(vp, { mobile: 14, tablet: 20, desktop: 26 }),
        zIndex: 20,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: byViewport(vp, { mobile: 10, desktop: 14 }),
          flexWrap: 'wrap',
          marginBottom: byViewport(vp, { mobile: 12, desktop: 18 }),
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Índice do deck</div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            height: byViewport(vp, { mobile: 44, desktop: 34 }),
            padding: '0 12px',
            borderRadius: 7,
            border: '1px solid rgba(255,255,255,0.18)',
            background: 'rgba(255,255,255,0.06)',
            flex: '1 1 200px',
            // No celular o campo ganha a linha inteira: com 300px de teto ele
            // dividia a linha com o título e sobrava um campo de 150px.
            maxWidth: byViewport<string | number>(vp, { mobile: '100%', desktop: 300 }),
          }}
        >
          <span style={{ color: 'rgba(255,255,255,0.55)', display: 'flex' }}>
            <IconSearch size={14} />
          </span>
          <input
            value={q}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQ(e.target.value)}
            placeholder="Buscar plataforma…"
            style={{
              flex: 1,
              // Mesma razão do campo do popup de filtros: a caixa do <input>
              // acompanha a altura da linha, senão o alvo de toque é metade dela.
              alignSelf: byViewport(vp, { mobile: 'stretch', desktop: 'auto' }),
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#fff',
              fontSize: 12.5,
              fontFamily: fontSans,
            }}
          />
        </div>
        <div style={{ flex: 1 }} />
        <ChromeButton onClick={onClose} title="Fechar (Esc)" grow={vp.mobile}>
          <IconX size={13} /> Fechar
        </ChromeButton>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <div
          style={{
            display: 'grid',
            // No celular a coluna mínima de 240px sobraria da largura útil (292px
            // em 320): `1fr` puro dá uma coluna que cabe em qualquer telefone.
            // 330px de coluna (era 240): num telão os cards ficavam miúdos e o
            // nome da plataforma truncava cedo demais.
            gridTemplateColumns: byViewport(vp, {
              mobile: '1fr',
              desktop: 'repeat(auto-fill, minmax(330px, 1fr))',
            }),
            gap: 10,
          }}
        >
          {hits.map(({ s, i }) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onPick(i)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                // 10px de recuo davam um item de 39px — 1px abaixo do alvo de
                // toque. No compacto vira 13px e o item passa dos 44px.
                padding: `${byViewport(vp, { mobile: 13, desktop: 14 })}px 14px`,
                borderRadius: 8,
                border: `1px solid ${i === current ? DECK.red : 'rgba(255,255,255,0.12)'}`,
                background: i === current ? 'rgba(197,9,5,0.18)' : 'rgba(255,255,255,0.04)',
                color: '#fff',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: fontSans,
              }}
            >
              <span
                style={{
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: 'rgba(255,255,255,0.5)',
                  width: 30,
                  flexShrink: 0,
                }}
              >
                {String(i + 1).padStart(2, '0')}
              </span>
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 14.5,
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {s.label}
              </span>
              {s.sublabel && (
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    padding: '4px 8px',
                    borderRadius: 3,
                    background: s.color,
                    color: '#fff',
                    flexShrink: 0,
                  }}
                >
                  {s.sublabel}
                </span>
              )}
            </button>
          ))}
        </div>
        {hits.length === 0 && (
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, padding: 20 }}>
            Nenhuma plataforma corresponde à busca.
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Alterna tela cheia NO ELEMENTO DA APRESENTAÇÃO — não no documento. Fullscreen
 * no `documentElement` só maximizava a aba: as barras de navegação continuavam
 * na tela. Com o root do deck em fullscreen, o CSS (`[data-fs='true']`) esconde
 * o chrome e o slide ocupa a tela inteira, como numa apresentação de verdade.
 * Fica fora do componente porque não depende de estado — o handler de teclado
 * a referencia sem virar dependência de efeito.
 */
async function toggleFullscreen(el: HTMLElement | null): Promise<void> {
  try {
    if (document.fullscreenElement) await document.exitFullscreen();
    else if (el) await el.requestFullscreen();
  } catch {
    // Navegador pode recusar sem gesto do usuário — seguir sem tela cheia.
  }
}

/** Espera fontes e imagens antes de abrir a impressão — senão o PDF sai com buracos. */
async function waitForAssets(root: HTMLElement | null): Promise<void> {
  // `document.fonts` pode não existir (navegador antigo): o `?.` desliga a
  // espera em vez de estourar. O `if` que havia aqui testava a PROMESSA
  // `document.fonts.ready` como condição — sempre verdadeira, então o teste não
  // media a existência da API que se queria checar.
  await document.fonts?.ready.catch(() => undefined);
  const images = Array.from(root?.querySelectorAll('img') ?? []);
  await Promise.all(
    images.map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            img.addEventListener('load', () => resolve(), { once: true });
            img.addEventListener('error', () => resolve(), { once: true });
          }),
    ),
  );
}

// ── Filtros do deck (compartilhados entre o lobby e a apresentação) ─────────

/**
 * Estado e derivação dos filtros do deck. Vive FORA do ScreenPresentation para
 * o LOBBY (tela de preparação) e o deck usarem exatamente o mesmo recorte: o
 * que se monta no lobby é o que se apresenta — e o que sai no PDF.
 *
 * Multisseleção dentro do grupo (OU) e interseção entre grupos (E) — a mesma
 * semântica do painel de filtros do catálogo, para não haver duas gramáticas
 * de filtro no produto.
 *
 * Além dos filtros há a ESCOLHA manual de quem entra no deck, e as duas coisas
 * COMPÕEM em vez de competir: o filtro define o universo (`recorteApps`) e a
 * escolha subtrai indivíduos dentro dele (`deckApps`). Como `deckApps` continua
 * sendo a lista final, tudo que já dependia dela — capa, visão, encerramento,
 * numeração, índice, progresso e PDF — passa a refletir a escolha sem nenhuma
 * mudança nos consumidores.
 */
export function useDeckFilters(apps: App[]) {
  /**
   * A escolha plataforma a plataforma é a coluna `apresentacaoDesabilitada` da
   * FICHA, não estado desta tela. Desmarcar no lobby grava na aplicação (PATCH)
   * e marcar de volta apaga; o mesmo check aparece na edição do cadastro como
   * "Desabilitar apresentação". Uma fonte da verdade, dois lugares para mexer.
   *
   * Era um `Set` em memória ("quem eu vou apresentar hoje é pergunta do
   * momento") e mudou por pedido explícito: o que se desmarca no lobby tem de
   * sumir da apresentação DE VEZ — sobrevive a sair da tela, a fechar o
   * navegador e vale para quem apresentar depois, porque está no banco. O
   * padrão continua sendo "vai todas": a coluna nasce `false` e o filtro nunca
   * mexe nela (plataforma que sai e volta ao recorte volta como estava).
   *
   * A escrita é otimista (`useSetApresentacaoDesabilitada`): `apps` vem do
   * cache de `['applications']`, então a caixinha vira no clique e `deckApps`
   * acompanha sem estado local. Se a API recusar (rede, ou colaborador sem
   * permissão — o PATCH é de administrador), a linha volta e um toast avisa.
   */
  const { mutate: gravarMarcacao } = useSetApresentacaoDesabilitada();
  const [statusSel, setStatusSel] = useState<string[]>([]);
  const [areaSel, setAreaSel] = useState<string[]>([]);
  const [typeSel, setTypeSel] = useState<string[]>([]);
  const [critSel, setCritSel] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const currentYear = new Date().getFullYear();
  // Período do deck: o ano corrente vem selecionado por padrão; o intervalo de
  // meses só entra quando o usuário mexe nos selects "De/Até".
  const [rawPeriod, setRawPeriod] = useState<DeckPeriod>({ mode: 'year', year: currentYear });
  const period = normalizeDeckPeriod(rawPeriod);
  const periodActive = period.mode === 'range' || period.year !== currentYear;
  const yearsLabel = deckPeriodYears(period);

  const toggleIn = (setter: React.Dispatch<React.SetStateAction<string[]>>) => (id: string) =>
    setter((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));

  const q = query.trim().toLowerCase();
  const matchQuery = (a: App) =>
    !q || [a.name, deckAreaOf(a), a.owner, a.description].some((f) => f.toLowerCase().includes(q));
  const matchStatus = (a: App) => statusSel.length === 0 || statusSel.includes(deckStatusOf(a).key);
  // Filtra pela ÁREA SOLICITANTE de verdade (`projetoArea`), que é o que o
  // rótulo do grupo promete. Filtrava por `team` — o time que constrói — e o
  // filtro oferecia uma opção só, "Desenvolvimento", em 48 das 49 fichas.
  const matchArea = (a: App) => areaSel.length === 0 || areaSel.includes(deckAreaOf(a));
  const matchType = (a: App) => typeSel.length === 0 || typeSel.includes(a.type);
  const matchCrit = (a: App) => critSel.length === 0 || critSel.includes(a.criticality);
  const matchPeriod = (a: App) => appInDeckPeriod(a, period);

  /**
   * O portfólio INTEIRO na ordem que vale agora: por área solicitante, ou a que
   * o apresentador montou arrastando. Ordenar aqui, antes de filtrar, é o que
   * faz a ordem valer igual no lobby, no deck e no PDF — e é a lista completa
   * que o arrasto salva, para o filtro ligado não apagar a posição de quem está
   * escondido no momento.
   */
  const { ordem, personalizada, salvarOrdem, restaurarPadrao } = useDeckOrder();
  const todasNaOrdem = deckAplicarOrdem(apps, ordem);

  /** O universo que os filtros deixaram — a lista que o lobby oferece para marcar. */
  const recorteApps = todasNaOrdem.filter(
    (a) =>
      matchQuery(a) &&
      matchPeriod(a) &&
      matchStatus(a) &&
      matchArea(a) &&
      matchType(a) &&
      matchCrit(a),
  );

  /** Solta `idArrastado` na posição de `idAlvo` e guarda a sequência inteira. */
  const reordenar = (idArrastado: string, idAlvo: string) =>
    salvarOrdem(
      deckMoverNaOrdem(
        todasNaOrdem.map((a) => a.id),
        idArrastado,
        idAlvo,
      ),
    );
  /** A lista que vai ao ar: o recorte menos quem está com a apresentação desabilitada. */
  const deckApps = recorteApps.filter((a) => !a.apresentacaoDesabilitada);
  const desmarcadasNoRecorte = recorteApps.length - deckApps.length;

  const porId = new Map(apps.map((a) => [a.id, a]));
  const marcada = (id: string) => !porId.get(id)?.apresentacaoDesabilitada;
  /** Grava o flag na ficha; `on` = marcada = apresentação HABILITADA. */
  const gravarMarcada = (id: string, on: boolean) =>
    gravarMarcacao(
      { id, desabilitada: !on },
      {
        onError: (error) =>
          toast.error(
            error.message ||
              (on
                ? 'Não foi possível devolver a plataforma à apresentação'
                : 'Não foi possível tirar a plataforma da apresentação'),
          ),
      },
    );
  const alternarMarcada = (id: string) => gravarMarcada(id, !marcada(id));
  /**
   * Marca/desmarca um lote de uma vez ("Marcar todas"/"Nenhuma"). Um PATCH por
   * plataforma que MUDA — quem já está no estado pedido não gera chamada.
   */
  const marcarTodas = (ids: readonly string[], on: boolean) => {
    for (const id of ids) {
      if (marcada(id) !== on) gravarMarcada(id, on);
    }
  };

  const activeFilters =
    statusSel.length + areaSel.length + typeSel.length + critSel.length + (periodActive ? 1 : 0);
  // Limpa SÓ os filtros: a escolha manual continua de pé de propósito. Quem
  // desmarcou 40 plataformas uma a uma e depois trocou o período não pode
  // perder o trabalho por clicar num botão que promete mexer em filtro; a
  // lista de plataformas tem o próprio "Marcar todas" para desfazer.
  const clearFilters = () => {
    setStatusSel([]);
    setAreaSel([]);
    setTypeSel([]);
    setCritSel([]);
    setQuery('');
    setRawPeriod({ mode: 'year', year: currentYear });
  };

  // Contadores facetados: cada grupo conta sobre a base filtrada pelos OUTROS
  // grupos (a faceta não conta a si mesma), então o número do chip diz quantas
  // plataformas sobram ao ligá-lo — e não some ao selecionar um irmão. A
  // escolha manual fica FORA desta conta: o chip descreve o que o filtro faz,
  // e descontar dele quem foi desmarcado tornaria o número impossível de ler.
  const baseFor = (skip: 'status' | 'area' | 'type' | 'crit' | 'period') =>
    apps.filter(
      (a) =>
        matchQuery(a) &&
        (skip === 'period' || matchPeriod(a)) &&
        (skip === 'status' || matchStatus(a)) &&
        (skip === 'area' || matchArea(a)) &&
        (skip === 'type' || matchType(a)) &&
        (skip === 'crit' || matchCrit(a)),
    );

  // Pílulas de ano: os anos com atividade cadastrada + o corrente (que é o
  // padrão e precisa existir mesmo num portfólio ainda sem atualização no ano).
  const yearOptions: YearOption[] = [...new Set([currentYear, ...deckAvailableYears(apps)])]
    .sort((a, b) => b - a)
    .map((y) => ({
      year: y,
      count: baseFor('period').filter((a) => appInDeckPeriod(a, { mode: 'year', year: y })).length,
    }));

  const groups: FilterGroup[] = [
    {
      id: 'status',
      label: 'Status',
      hint: 'a pílula do slide',
      options: DECK_STATUS_ORDER.filter((k) => apps.some((a) => deckStatusOf(a).key === k)).map(
        (k) => ({
          id: k,
          label: DECK_STATUS[k].label,
          color: DECK_STATUS[k].color,
          count: baseFor('status').filter((a) => deckStatusOf(a).key === k).length,
        }),
      ),
      selected: statusSel,
      toggle: toggleIn(setStatusSel),
    },
    {
      id: 'area',
      label: 'Área solicitante',
      hint: 'quem pediu a plataforma',
      // Mesma ordem da lista do lobby e do deck: alfabética, com o grupo sem
      // área no fim — e não no meio do alfabeto, pelo "S" de "Sem".
      options: [...new Set(apps.map(deckAreaOf))]
        .sort((a, b) => {
          if (a === DECK_SEM_AREA) return 1;
          if (b === DECK_SEM_AREA) return -1;
          return a.localeCompare(b, 'pt-BR');
        })
        .map((t) => ({
          id: t,
          label: t,
          count: baseFor('area').filter((a) => deckAreaOf(a) === t).length,
        })),
      selected: areaSel,
      toggle: toggleIn(setAreaSel),
    },
    {
      id: 'type',
      label: 'Tipo de plataforma',
      hint: 'como foi construída',
      options: Object.keys(APP_TYPES)
        .filter((t) => apps.some((a) => a.type === t))
        .map((t) => ({
          id: t,
          label: APP_TYPES[t].label,
          count: baseFor('type').filter((a) => a.type === t).length,
        })),
      selected: typeSel,
      toggle: toggleIn(setTypeSel),
    },
    {
      id: 'crit',
      label: 'Criticidade',
      hint: 'impacto no negócio',
      options: ['alta', 'media', 'baixa']
        .filter((c) => apps.some((a) => a.criticality === c))
        .map((c) => ({
          id: c,
          label: CRITICALITY_LABELS[c] ?? c,
          count: baseFor('crit').filter((a) => a.criticality === c).length,
        })),
      selected: critSel,
      toggle: toggleIn(setCritSel),
    },
  ];

  return {
    query,
    setQuery,
    q,
    rawPeriod,
    setRawPeriod,
    period,
    periodActive,
    yearsLabel,
    yearOptions,
    groups,
    recorteApps,
    deckApps,
    desmarcadasNoRecorte,
    marcada,
    alternarMarcada,
    marcarTodas,
    activeFilters,
    clearFilters,
    reordenar,
    ordemPersonalizada: personalizada,
    restaurarOrdem: restaurarPadrao,
  };
}

export type DeckFilters = ReturnType<typeof useDeckFilters>;

// ── Tela ────────────────────────────────────────────────────────────────────

export function ScreenPresentation({
  apps,
  filters,
  onExit,
  autoFullscreen = false,
}: Readonly<{
  apps: App[];
  filters: DeckFilters;
  /** Fechar a apresentação (X do chrome) — volta ao lobby. */
  onExit: () => void;
  /** Pede tela cheia ao montar — usado pelo "Iniciar apresentação" do lobby. */
  autoFullscreen?: boolean;
}>) {
  const vp = useViewport();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);

  // Custo do Railway por aplicação. A rota /admin/apresentacao é AdminOnly, e
  // custo é dado financeiro — o mesmo motivo pelo qual o catálogo só busca
  // isto para administrador. Aqui não precisa de flag: quem chegou já é admin.
  const { data: costs, isSuccess: custosProntos } = useRailwayCosts();
  const custoPorSlug = costsByRepoSlug(costs?.custos);
  /**
   * Custo mensal da aplicação EM REAIS: o projeto do Railway é encontrado pelo
   * repositório do cadastro. Sem projeto correspondente o custo é ZERO, não
   * desconhecido — aplicação fora da nuvem não consome Railway. A distinção
   * fica em `custosProntos`: enquanto a consulta não voltou, TUDO é `null`
   * ("—"), porque afirmar zero com o dado ausente é mentir em 43 slides.
   *
   * POR QUE A CONVERSÃO ACONTECE AQUI, uma vez por render, e não dentro de cada
   * slide: a cotação é a MESMA para o deck inteiro, e ela vem no mesmo envelope
   * do custo (`GET /api/railway/costs`), já buscada e cacheada pelo servidor —
   * o navegador não fala com o Banco Central em slide nenhum.
   *
   * SEM COTAÇÃO o valor é `null` e o slide mostra "—". Não existe caminho que
   * caia de volta para dólar: o pedido é que dólar não apareça na apresentação,
   * e converter por uma taxa que não temos seria projetar número inventado. O
   * rótulo "Custo mensal · Railway" continua no lugar, então o custo não some
   * em silêncio: ele diz que existe e que agora não se sabe o valor.
   */
  const custoDeApp = (a: App): number | null => {
    if (!custosProntos) return null;
    const slug = repoSlug(a.repo);
    const usd = slug ? (custoPorSlug.get(slug)?.estimadoMesUsd ?? 0) : 0;
    return converterParaBrl(usd, costs?.cotacao);
  };
  const [scale, setScale] = useState(0.6);
  const [idx, setIdx] = useState(0);
  /**
   * Tela do carrossel da captura. Mora aqui, e não dentro do `SlideShot`,
   * porque é o teclado da apresentação (que vive neste componente) quem avança
   * a imagem.
   *
   * A posição carrega a VISITA a que pertence, e `shotPos` é DERIVADO dela: ao
   * trocar de slide o carrossel volta à capa sozinho, sem efeito de reset — um
   * `useEffect(() => setPos(0), [idx])` renderizaria a 4ª tela por um frame
   * antes de voltar, e num projetor isso pisca na cara da sala.
   *
   * VISITA, e não o índice do slide: keyar pelo índice guarda a posição DAQUELE
   * slide, então sair e VOLTAR reencontrava a tela onde o apresentador havia
   * parado em vez da abertura — medido no navegador, voltava em "2 / 4". Quem
   * volta a um slide está retomando a fala dele desde o começo.
   *
   * O contador sobe durante o RENDER, comparando com o índice anterior (o
   * idioma de ajuste de estado derivado do React), e não em efeito — pelo mesmo
   * motivo do parágrafo acima: efeito pinta o quadro errado antes de corrigir.
   */
  const [shot, setShot] = useState<{ visita: number; pos: number }>({ visita: 0, pos: 0 });
  // Espelho da visita para o callback NÃO depender de `idx`: `PlatformSlide` é
  // memo(), e um `onShotStep` novo a cada troca de slide invalidaria os 44 de
  // uma vez — justamente o engasgo que o memo existe para evitar.
  const visitaRef = useRef(0);
  const idxAnteriorRef = useRef(idx);
  if (idxAnteriorRef.current !== idx) {
    idxAnteriorRef.current = idx;
    visitaRef.current += 1;
  }
  const shotPos = shot.visita === visitaRef.current ? shot.pos : 0;
  const passarTela = useCallback((delta: number) => {
    setShot((s) => {
      const visita = visitaRef.current;
      return { visita, pos: (s.visita === visita ? s.pos : 0) + delta };
    });
  }, []);
  const [indexOpen, setIndexOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const {
    query,
    setQuery,
    q,
    rawPeriod,
    setRawPeriod,
    yearsLabel,
    period,
    yearOptions,
    groups,
    deckApps,
    desmarcadasNoRecorte,
    activeFilters,
    clearFilters,
  } = filters;
  // Gasto mensal do RECORTE, para a tela de visão — depende de `deckApps`, que
  // só existe depois da desestruturação acima.
  const nuvem = gastoDaNuvem(deckApps, custoPorSlug, costs?.cotacao, custosProntos);
  // O deck não é o portfólio inteiro quando um filtro apertou OU quando o
  // apresentador desmarcou plataformas no lobby — nos dois casos o cabeçalho
  // diz de quantas o número saiu, para ninguém achar que sumiu cadastro.
  const recortado = activeFilters > 0 || q !== '' || desmarcadasNoRecorte > 0;

  // Tela cheia já na entrada: o clique em "Iniciar apresentação" carrega a
  // ativação de usuário, então o requestFullscreen do primeiro paint é aceito.
  // biome-ignore lint/correctness/useExhaustiveDependencies: só na montagem — mudar o flag depois não deve alternar a tela cheia.
  useEffect(() => {
    if (autoFullscreen) void toggleFullscreen(rootRef.current);
  }, []);

  // Capa + visão + uma por plataforma + encerramento. É o plano que manda na
  // navegação, no índice, na contagem da barra de progresso e na impressão — a
  // numeração da PLATAFORMA continua vindo do `appIndex`, e não da posição do
  // slide, para que nenhum slide intercalado empurre "Plataforma nº" de ninguém.
  const plano = deckSlidePlan(deckApps);
  const total = plano.length;
  const slides: DeckSlide[] = plano.map((item) => {
    if (item.kind === 'cover') return { id: 'cover', label: 'Capa' };
    if (item.kind === 'vision') return { id: 'vision', label: 'Visão do portfólio' };
    if (item.kind === 'closing') return { id: 'closing', label: 'Encerramento' };
    const a = deckApps[item.appIndex];
    const s = deckStatusOf(a);
    return { id: a.id, label: a.name, sublabel: s.label, color: s.color };
  });

  // Trocar o filtro encurta o deck: volta para a visão em vez de deixar o
  // índice apontando para um slide que não existe mais.
  useEffect(() => {
    setIdx((i) => Math.min(i, Math.max(0, total - 1)));
  }, [total]);

  // Espelha o estado real do fullscreen (o usuário pode sair com Esc, que o
  // navegador trata sozinho — sem este listener o chrome não voltaria).
  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement != null);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // O slide tem tamanho fixo (1440×810) e é escalado para caber no palco —
  // assim as coordenadas do PowerPoint valem em qualquer monitor. É a MESMA
  // conta em toda faixa (16:9 preservado, `Math.min` dos dois eixos); o que muda
  // no compacto é a largura de referência, que inclui a sangria da capa
  // (`DECK_BLEED`) para nenhuma peça do slide parar fora da tela do telefone.
  const stageRoom = byViewport(vp, { mobile: DECK_W + DECK_BLEED * 2, desktop: DECK_W });
  // Alvos de toque das duas barras e do controle flutuante: 40px no compacto
  // (mesma medida do `NavToggle` do resto do produto), os valores de hoje no
  // ponteiro. Ficam aqui em cima porque as três barras precisam combinar.
  const chromeBtn = byViewport(vp, { mobile: 40, desktop: 34 });
  const fsBtn = byViewport(vp, { mobile: 40, desktop: 32 });
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) setScale(Math.min(width / stageRoom, height / DECK_H));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [stageRoom]);

  // Com um popup aberto o teclado pertence a ele: só Esc responde, senão as
  // setas passariam por baixo do modal virando slide sem o usuário ver.
  const modalOpen = indexOpen || filtersOpen;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      const digitando = tag === 'INPUT' || tag === 'TEXTAREA';
      if (modalOpen) {
        // Esc fecha o popup MESMO com o foco na busca dele — o guard de
        // digitação vinha antes e prendia o usuário no modal (só o X fechava).
        // As demais teclas seguem do popup: nada vira slide por baixo.
        if (e.key === 'Escape') {
          setIndexOpen(false);
          setFiltersOpen(false);
        }
        return;
      }
      if (digitando) return;
      const next = () => setIdx((i) => Math.min(i + 1, total - 1));
      const prev = () => setIdx((i) => Math.max(i - 1, 0));
      switch (e.key) {
        case 'ArrowRight':
        case 'PageDown':
        case ' ':
          e.preventDefault();
          next();
          break;
        case 'ArrowLeft':
        case 'PageUp':
          e.preventDefault();
          prev();
          break;
        // As telas da aplicação ficam no eixo VERTICAL porque o horizontal já é
        // do slide: ←/→ (e espaço) continuam virando slide, como qualquer
        // apresentação, e ↓/↑ percorrem o carrossel da captura. Quem não tem
        // galeria não percebe diferença — o slide ignora.
        case 'ArrowDown':
          e.preventDefault();
          passarTela(1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          passarTela(-1);
          break;
        case 'Home':
          e.preventDefault();
          setIdx(0);
          break;
        case 'End':
          e.preventDefault();
          setIdx(total - 1);
          break;
        case 'g':
        case 'G':
          setIndexOpen(true);
          break;
        case 'f':
        case 'F':
          void toggleFullscreen(rootRef.current);
          break;
        case 'l':
        case 'L':
          setFiltersOpen(true);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [total, modalOpen, passarTela]);

  async function downloadPdf() {
    setPreparing(true);
    const previousTitle = document.title;
    document.title = deckDocumentTitle();
    try {
      await waitForAssets(rootRef.current);
      window.print();
    } finally {
      document.title = previousTitle;
      setPreparing(false);
    }
  }

  /**
   * Um slide do plano. Cada plataforma rende o slide da tela MAIS as páginas de
   * impressão das telas extras — irmãs, não filhas, porque `.deck-slide` recorta
   * em 810px de altura e uma página aninhada sairia cortada no PDF.
   */
  function renderSlide(item: DeckPlanItem, i: number) {
    const ativo = idx === i;
    if (item.kind === 'cover') {
      return (
        <div key="cover" className="deck-slide" data-active={ativo}>
          <CoverSlide period={period} subtitle="Dados, IA e Inovação" />
        </div>
      );
    }
    if (item.kind === 'vision') {
      return (
        <div key="vision" className="deck-slide" data-active={ativo}>
          <VisionSlide apps={deckApps} period={period} nuvem={nuvem} />
        </div>
      );
    }
    if (item.kind === 'closing') {
      return (
        <div key="closing" className="deck-slide" data-active={ativo}>
          <ClosingSlide total={deckApps.length} years={yearsLabel} />
        </div>
      );
    }
    const app = deckApps[item.appIndex];
    const telas = applicationImageUrls(app, 1280);
    return (
      <Fragment key={app.id}>
        <div className="deck-slide" data-active={ativo}>
          <PlatformSlide
            app={app}
            index={item.appIndex}
            years={yearsLabel}
            custoBrl={custoDeApp(app)}
            // Só o slide ativo recebe a posição do carrossel: nos demais ela é
            // constante, e o memo() os mantém fora do re-render.
            shotPos={ativo ? shotPos : 0}
            onShotStep={passarTela}
          />
        </div>
        {telas.slice(1).map((url, k) => (
          <ShotPrintSlide
            key={url}
            app={app}
            index={item.appIndex}
            years={yearsLabel}
            url={url}
            ordem={k + 2}
            total={telas.length}
          />
        ))}
      </Fragment>
    );
  }

  return (
    <div className="deck-root" ref={rootRef} data-fs={isFullscreen}>
      {/* biome-ignore lint/security/noDangerouslySetInnerHtml: CSS estático interno (@page/@media print), sem entrada do usuário. */}
      <style dangerouslySetInnerHTML={{ __html: DECK_CSS }} />

      {/* Barra superior */}
      <div
        className="deck-chrome"
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: byViewport(vp, { mobile: 10, desktop: 12 }),
          // Em tela estreita os botões passam para a segunda linha em vez de
          // sangrar pela direita (o "Baixar PDF" era o primeiro a sumir).
          flexWrap: 'wrap',
          padding: byViewport(vp, { mobile: '10px 12px', desktop: '12px 18px' }),
          borderBottom: '1px solid rgba(255,255,255,0.09)',
          background: '#111114',
        }}
      >
        <button
          type="button"
          onClick={onExit}
          title="Encerrar apresentação"
          aria-label="Encerrar apresentação"
          style={{
            width: chromeBtn,
            height: chromeBtn,
            borderRadius: 7,
            border: '1px solid rgba(255,255,255,0.18)',
            background: 'rgba(255,255,255,0.06)',
            color: '#fff',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconX size={14} />
        </button>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 700,
              color: '#fff',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Apresentação do portfólio
          </div>
          <div
            style={{
              fontSize: 11,
              color: 'rgba(255,255,255,0.5)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {deckApps.length} {deckApps.length === 1 ? 'plataforma' : 'plataformas'} · {total}{' '}
            slides
            {recortado ? ` · de ${apps.length} no portfólio` : ''}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            marginLeft: 'auto',
            // No celular o grupo desce para uma linha só dele e os quatro botões
            // se dividem em dois pares largos; no tablet e no desktop continua
            // encostado à direita da primeira linha, como hoje.
            flexBasis: byViewport(vp, { mobile: '100%', tablet: 'auto', desktop: 'auto' }),
          }}
        >
          <ChromeButton
            onClick={() => setFiltersOpen(true)}
            title="Filtros do deck (L)"
            grow={vp.mobile}
          >
            <IconFilter size={13} /> Filtros
            {activeFilters > 0 && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '1px 6px',
                  borderRadius: 999,
                  background: DECK.red,
                  color: '#fff',
                }}
              >
                {activeFilters}
              </span>
            )}
          </ChromeButton>
          <ChromeButton onClick={() => setIndexOpen(true)} title="Índice (G)" grow={vp.mobile}>
            <IconGrid size={13} /> Índice
          </ChromeButton>
          <ChromeButton
            onClick={() => void toggleFullscreen(rootRef.current)}
            title="Tela cheia (F)"
            grow={vp.mobile}
          >
            Tela cheia
          </ChromeButton>
          <ChromeButton
            onClick={() => void downloadPdf()}
            primary
            disabled={preparing}
            grow={vp.mobile}
          >
            {preparing ? 'Preparando…' : 'Baixar PDF'}
          </ChromeButton>
        </div>
      </div>

      {/* Palco */}
      <div className="deck-stage" ref={stageRef}>
        <div
          className="deck-viewport"
          style={{ ['--deck-scale' as string]: scale } as CSSProperties}
        >
          {plano.map(renderSlide)}
        </div>
      </div>

      {/* Controle flutuante do modo tela cheia: quase invisível até o hover,
          para não competir com o slide projetado. Esc/F saem do modo.
          No toque NÃO EXISTE hover: no celular o controle ficaria em 25% de
          opacidade para sempre, e ele é a única forma de virar slide em tela
          cheia (não há teclado). Por isso nasce visível no compacto — o inline
          vence a classe; no ponteiro fica `undefined` e o :hover da classe
          continua mandando, como hoje. */}
      {isFullscreen && (
        <div
          className="deck-fs-nav"
          style={{ opacity: byViewport<number | undefined>(vp, { mobile: 1, desktop: undefined }) }}
        >
          <button
            type="button"
            className="deck-fs-btn"
            title="Anterior (←)"
            aria-label="Slide anterior"
            style={{ width: fsBtn, height: fsBtn }}
            onClick={() => setIdx((i) => Math.max(i - 1, 0))}
          >
            <ChevronBack size={15} />
          </button>
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: '#fff',
              padding: '0 8px',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {idx + 1} <span style={{ color: 'rgba(255,255,255,0.45)' }}>/ {total}</span>
          </span>
          <button
            type="button"
            className="deck-fs-btn"
            title="Próximo (→ ou espaço)"
            aria-label="Próximo slide"
            style={{ width: fsBtn, height: fsBtn }}
            onClick={() => setIdx((i) => Math.min(i + 1, total - 1))}
          >
            <IconChevronR size={15} />
          </button>
          <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.18)' }} />
          <button
            type="button"
            className="deck-fs-btn"
            title="Sair da tela cheia (Esc)"
            aria-label="Sair da tela cheia"
            style={{ width: fsBtn, height: fsBtn }}
            onClick={() => void toggleFullscreen(rootRef.current)}
          >
            <IconX size={14} />
          </button>
        </div>
      )}

      {/* Barra inferior. No celular ela vira três linhas — Anterior/Próximo
          esticados com o contador, a barra de progresso e o nome do slide — em
          vez de cinco peças espremidas numa linha de 320px. */}
      <div
        className="deck-chrome"
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: byViewport(vp, { mobile: 10, desktop: 14 }),
          flexWrap: 'wrap',
          padding: byViewport(vp, { mobile: '10px 12px', desktop: '10px 18px' }),
          borderTop: '1px solid rgba(255,255,255,0.09)',
          background: '#111114',
        }}
      >
        <ChromeButton
          onClick={() => setIdx((i) => Math.max(i - 1, 0))}
          title="Anterior (←)"
          grow={vp.mobile}
        >
          <ChevronBack size={14} /> Anterior
        </ChromeButton>
        <ChromeButton
          onClick={() => setIdx((i) => Math.min(i + 1, total - 1))}
          title="Próximo (→ ou espaço)"
          grow={vp.mobile}
        >
          Próximo <IconChevronR size={14} />
        </ChromeButton>

        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: '#fff',
            // 78px de largura mínima roubavam a linha dos dois botões no celular.
            minWidth: byViewport(vp, { mobile: 52, desktop: 78 }),
            textAlign: 'center',
          }}
        >
          {idx + 1} <span style={{ color: 'rgba(255,255,255,0.4)' }}>/ {total}</span>
        </div>

        <div
          style={{
            // Base de 100% no celular = linha própria para o progresso.
            flex: byViewport(vp, { mobile: '1 1 100%', desktop: '1 1 120px' }),
            minWidth: 80,
            height: 4,
            borderRadius: 2,
            background: 'rgba(255,255,255,0.12)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${total > 1 ? (idx / (total - 1)) * 100 : 100}%`,
              height: '100%',
              background: DECK.red,
              transition: 'width 180ms',
            }}
          />
        </div>

        {/* Nome do slide atual: encolhe até sumir antes de empurrar a barra. No
            celular ganha a própria linha, centralizado — é o rótulo que diz onde
            a pessoa está no deck quando o slide é um cartão de 340px. */}
        <div
          style={{
            flex: byViewport(vp, { mobile: '1 1 100%', desktop: '0 1 220px' }),
            minWidth: 0,
            fontSize: 11.5,
            color: 'rgba(255,255,255,0.6)',
            textAlign: byViewport(vp, { mobile: 'center', desktop: 'right' }),
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {/* O rótulo sai do PLANO, não da aplicação ativa: é o mesmo texto que
              o índice lista, e a capa, a visão e o encerramento — que não têm
              aplicação nenhuma — também precisam se anunciar aqui. */}
          {slides[idx]?.label}
        </div>
      </div>

      {indexOpen && (
        <IndexOverlay
          slides={slides}
          current={idx}
          onPick={(i) => {
            setIdx(i);
            setIndexOpen(false);
          }}
          onClose={() => setIndexOpen(false)}
        />
      )}

      {filtersOpen && (
        <DeckFilterModal
          groups={groups}
          query={query}
          setQuery={setQuery}
          period={rawPeriod}
          setPeriod={setRawPeriod}
          yearOptions={yearOptions}
          shown={deckApps.length}
          total={apps.length}
          desmarcadas={desmarcadasNoRecorte}
          activeCount={activeFilters + (q ? 1 : 0)}
          onClear={clearFilters}
          onClose={() => setFiltersOpen(false)}
        />
      )}
    </div>
  );
}
